import React from 'react';
import PanoramaViewer from '../editor/PanoramaViewer';
import { CloudApiError, cloudApi, publicApiPath } from './api';
import { autoTranslateTexts } from './autoTranslate';
import { getCloudConfig, publicTourIdFromPath } from './config';
import { uploadFiles } from './uploads';
import { hasPendingAssets, useAssetStatusPolling } from './useAssetStatusPolling';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'vr-tour-cloud-project-sidebar-collapsed';
const CJK_PATTERN = /[\u3400-\u9fff]/u;

function emptyTranslations() {
  return {
    en: {
      title: '',
      panoNames: {},
      stationNamesByPano: {},
      folderNames: {},
    },
  };
}

function initialSidebarCollapsed() {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function createId(prefix) {
  const random = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now()}-${random}`;
}

function emptyDocument() {
  return {
    schemaVersion: 3,
    panos: [],
    currentId: null,
    stationsByPano: {},
    sceneFolders: [],
    translations: emptyTranslations(),
  };
}

function cloneDocument(document) {
  return JSON.parse(JSON.stringify(document || emptyDocument()));
}

function fileLabel(name = '') {
  return String(name).replace(/\.[^.]+$/, '') || '未命名全景';
}

function moveById(items, sourceId, targetId, position = 'before') {
  const sourceIndex = items.findIndex((item) => item.id === sourceId);
  if (sourceIndex < 0 || sourceId === targetId) return items;
  const next = [...items];
  const [moved] = next.splice(sourceIndex, 1);
  const targetIndex = next.findIndex((item) => item.id === targetId);
  if (targetIndex < 0) return items;
  next.splice(targetIndex + (position === 'after' ? 1 : 0), 0, moved);
  return next;
}

function moveValue(items, sourceValue, targetValue, position = 'before') {
  const sourceIndex = items.indexOf(sourceValue);
  if (sourceIndex < 0 || sourceValue === targetValue) return items;
  const next = [...items];
  const [moved] = next.splice(sourceIndex, 1);
  const targetIndex = next.indexOf(targetValue);
  if (targetIndex < 0) return items;
  next.splice(targetIndex + (position === 'after' ? 1 : 0), 0, moved);
  return next;
}

function pointerDropPosition(event) {
  const bounds = event.currentTarget.getBoundingClientRect();
  return event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after';
}

function dateLabel(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? String(value) : date.toLocaleString();
}

function percent(loaded, total) {
  if (!total) return 0;
  return Math.min(100, Math.max(0, Math.round((loaded / total) * 100)));
}

function assetStatus(value, tileState = null) {
  const status = String(value || '').toLowerCase();
  const tile = String(tileState || '').toLowerCase();
  if (status === 'ready' && (tile === 'queued' || tile === 'processing')) return '高清处理中';
  if (status === 'ready' && tile === 'failed') return '高清生成失败';
  if (status === 'ready') return '可用';
  if (status === 'queued') return '等待处理';
  if (status === 'processing') return '处理中';
  if (status === 'failed') return '处理失败';
  if (status === 'uploading' || status === 'uploaded') return '上传中';
  return status || '等待素材';
}

function cleanTranslatedText(value, maximum) {
  return String(value || '').trim().slice(0, maximum);
}

function normalizedTranslations(source, panos, stationsByPano, sceneFolders) {
  const english = source?.translations?.en && typeof source.translations.en === 'object'
    ? source.translations.en
    : {};
  const rawPanoNames = english.panoNames && typeof english.panoNames === 'object' ? english.panoNames : {};
  const rawFolderNames = english.folderNames && typeof english.folderNames === 'object' ? english.folderNames : {};
  const rawStationNames = english.stationNamesByPano && typeof english.stationNamesByPano === 'object'
    ? english.stationNamesByPano
    : {};
  return {
    en: {
      title: cleanTranslatedText(english.title, 120),
      panoNames: Object.fromEntries(
        panos
          .map((pano) => [pano.id, cleanTranslatedText(rawPanoNames[pano.id], 120)])
          .filter(([, name]) => name)
      ),
      stationNamesByPano: Object.fromEntries(
        Object.entries(stationsByPano)
          .map(([panoId, stations]) => {
            const names = rawStationNames[panoId] && typeof rawStationNames[panoId] === 'object'
              ? rawStationNames[panoId]
              : {};
            return [
              panoId,
              Object.fromEntries(
                stations
                  .map((station) => [station.id, cleanTranslatedText(names[station.id], 120)])
                  .filter(([, name]) => name)
              ),
            ];
          })
          .filter(([, names]) => Object.keys(names).length > 0)
      ),
      folderNames: Object.fromEntries(
        sceneFolders
          .map((folder) => [folder.id, cleanTranslatedText(rawFolderNames[folder.id], 60)])
          .filter(([, name]) => name)
      ),
    },
  };
}

function normalizeDocument(input) {
  const source = input && typeof input === 'object' ? input : emptyDocument();
  const panos = (Array.isArray(source.panos) ? source.panos : []).map((item, index) => ({
    id: String(item?.id || `scene-${index + 1}`),
    name: String(item?.name || `全景 ${index + 1}`),
    assetId: item?.assetId || null,
    entryAudioAssetId: item?.entryAudioAssetId || null,
    entryAudioName: item?.entryAudioName || null,
    entryAudioUrl: item?.entryAudioUrl || null,
    url: item?.url || item?.previewUrl || null,
    previewUrl: item?.previewUrl || item?.url || null,
    thumbnailUrl: item?.thumbnailUrl || null,
    status: item?.status || item?.state || null,
    width: item?.width || null,
    height: item?.height || null,
    tileState: item?.tileState || item?.tile_state || null,
    tileError: item?.tileError || item?.tile_error || null,
  }));
  const validPanoIds = new Set(panos.map((pano) => pano.id));
  const stationsByPano = {};
  panos.forEach((pano) => {
    const sourceStations = Array.isArray(source.stationsByPano?.[pano.id])
      ? source.stationsByPano[pano.id]
      : [];
    stationsByPano[pano.id] = sourceStations
      .filter((station) => validPanoIds.has(String(station?.targetPanoId || '')))
      .map((station, index) => ({
        id: String(station?.id || `station-${index + 1}`),
        name: String(station?.name || `导航点 ${index + 1}`),
        yaw: Number(station?.yaw) || 0,
        pitch: Number(station?.pitch) || 0,
        targetPanoId: String(station?.targetPanoId),
        audioAssetId: station?.audioAssetId || null,
        audioName: station?.audioName || null,
        audioUrl: station?.audioUrl || null,
      }));
  });
  const seenFolderIds = new Set();
  const sceneFolders = (Array.isArray(source.sceneFolders) ? source.sceneFolders : [])
    .slice(0, 50)
    .map((folder, index) => {
      const id = String(folder?.id || `folder-${index + 1}`);
      if (seenFolderIds.has(id)) return null;
      seenFolderIds.add(id);
      const seenPanoIds = new Set();
      const panoIds = (Array.isArray(folder?.panoIds) ? folder.panoIds : [])
        .map(String)
        .filter((panoId) => {
          if (!validPanoIds.has(panoId) || seenPanoIds.has(panoId)) return false;
          seenPanoIds.add(panoId);
          return true;
        });
      return {
        id,
        name: String(folder?.name || `文件夹 ${index + 1}`).trim().slice(0, 60) || `文件夹 ${index + 1}`,
        panoIds,
      };
    })
    .filter(Boolean);

  return {
    schemaVersion: 3,
    panos,
    currentId: validPanoIds.has(source.currentId) ? source.currentId : panos[0]?.id || null,
    stationsByPano,
    sceneFolders,
    translations: normalizedTranslations(source, panos, stationsByPano, sceneFolders),
  };
}

function serializableDocument(document) {
  const normalized = normalizeDocument(document);
  return {
    schemaVersion: 3,
    currentId: normalized.currentId,
    panos: normalized.panos.map((pano) => ({
      id: pano.id,
      name: pano.name,
      assetId: pano.assetId || null,
      entryAudioAssetId: pano.entryAudioAssetId || null,
    })),
    stationsByPano: Object.fromEntries(
      Object.entries(normalized.stationsByPano).map(([panoId, stations]) => [
        panoId,
        stations.map((station) => ({
          id: station.id,
          name: station.name,
          yaw: station.yaw,
          pitch: station.pitch,
          targetPanoId: station.targetPanoId,
          audioAssetId: station.audioAssetId || null,
        })),
      ])
    ),
    sceneFolders: normalized.sceneFolders.map((folder) => ({
      id: folder.id,
      name: folder.name,
      panoIds: [...folder.panoIds],
    })),
    translations: normalized.translations,
  };
}

function normalizeProject(input, fallback = {}) {
  const project = input?.project || input || {};
  const remoteDocument = project.document || project.draft || input?.document || input?.draft || fallback.document;
  return {
    id: project.id || fallback.id || null,
    title: project.title || fallback.title || '未命名项目',
    revision: Number(project.revision ?? fallback.revision ?? 0),
    updatedAt: project.updatedAt || project.updated_at || fallback.updatedAt || null,
    document: normalizeDocument(remoteDocument),
  };
}

function projectPollingKey(project) {
  return JSON.stringify([
    project?.id || null,
    project?.title || '',
    Number(project?.revision || 0),
    project?.document || null,
  ]);
}

function versionRows(payload) {
  const rows = Array.isArray(payload) ? payload : payload?.versions || [];
  return rows.map((version) => ({
    ...version,
    id: version.id || version.versionId,
    label: version.label || '云端保存',
    createdAt: version.createdAt || version.created_at,
  }));
}

function releasePublicId(release) {
  return release?.publicId || release?.public_id || release?.id || null;
}

function englishOrReusable(canonical, translated) {
  const value = String(translated || '').trim();
  if (value) return CJK_PATTERN.test(value) ? '' : value;
  const source = String(canonical || '').trim();
  return CJK_PATTERN.test(source) ? '' : source;
}

function localizedPanoName(document, pano, locale, placeholder = true) {
  if (!pano) return '';
  if (locale !== 'en') return pano.name;
  const value = englishOrReusable(pano.name, document.translations?.en?.panoNames?.[pano.id]);
  return value || (placeholder ? `未填写英文（${pano.name}）` : '');
}

function localizedFolderName(document, folder, locale, placeholder = true) {
  if (!folder) return '';
  if (locale !== 'en') return folder.name;
  const value = englishOrReusable(folder.name, document.translations?.en?.folderNames?.[folder.id]);
  return value || (placeholder ? `未填写英文（${folder.name}）` : '');
}

function localizedStationName(document, panoId, station, locale, placeholder = true) {
  if (!station) return '';
  if (locale !== 'en') return station.name;
  const value = englishOrReusable(
    station.name,
    document.translations?.en?.stationNamesByPano?.[panoId]?.[station.id]
  );
  return value || (placeholder ? `未填写英文（${station.name}）` : '');
}

function localizedProjectTitle(project, document, locale, placeholder = true) {
  if (!project) return '';
  if (locale !== 'en') return project.title;
  const value = englishOrReusable(project.title, document.translations?.en?.title);
  return value || (placeholder ? `未填写英文（${project.title}）` : '');
}

function englishTranslationIssues(project, document) {
  if (!project) return [];
  const issues = [];
  if (!localizedProjectTitle(project, document, 'en', false)) {
    issues.push(`项目标题「${project.title}」`);
  }
  document.panos.forEach((pano) => {
    if (!localizedPanoName(document, pano, 'en', false)) issues.push(`场景「${pano.name}」`);
  });
  document.sceneFolders.forEach((folder) => {
    if (!localizedFolderName(document, folder, 'en', false)) issues.push(`文件夹「${folder.name}」`);
  });
  Object.entries(document.stationsByPano).forEach(([panoId, stations]) => {
    stations.forEach((station) => {
      if (!localizedStationName(document, panoId, station, 'en', false)) {
        issues.push(`导航点「${station.name}」`);
      }
    });
  });
  return issues;
}

function missingEnglishTranslationEntries(project, document) {
  if (!project) return [];
  const entries = [];
  const add = (kind, source, translated, id = null, panoId = null) => {
    const canonical = String(source || '').trim();
    if (canonical && !englishOrReusable(canonical, translated)) {
      entries.push({ kind, id, panoId, source: canonical });
    }
  };

  add('title', project.title, document.translations?.en?.title);
  document.panos.forEach((pano) => {
    add('pano', pano.name, document.translations?.en?.panoNames?.[pano.id], pano.id);
  });
  document.sceneFolders.forEach((folder) => {
    add('folder', folder.name, document.translations?.en?.folderNames?.[folder.id], folder.id);
  });
  Object.entries(document.stationsByPano).forEach(([panoId, stations]) => {
    stations.forEach((station) => {
      add(
        'station',
        station.name,
        document.translations?.en?.stationNamesByPano?.[panoId]?.[station.id],
        station.id,
        panoId
      );
    });
  });
  return entries;
}

function applyAutoEnglishTranslations(project, document, entries, translations) {
  const next = cloneDocument(document);
  const english = next.translations?.en || emptyTranslations().en;
  next.translations = { ...(next.translations || {}), en: english };
  english.panoNames = { ...(english.panoNames || {}) };
  english.folderNames = { ...(english.folderNames || {}) };
  english.stationNamesByPano = { ...(english.stationNamesByPano || {}) };
  let applied = 0;

  entries.forEach((entry) => {
    const translated = cleanTranslatedText(translations.get(entry.source), entry.kind === 'folder' ? 60 : 120);
    if (!translated || CJK_PATTERN.test(translated)) return;

    if (entry.kind === 'title') {
      const source = String(project?.title || '').trim();
      if (source !== entry.source || englishOrReusable(source, english.title)) return;
      english.title = translated;
      applied += 1;
      return;
    }

    if (entry.kind === 'pano') {
      const pano = next.panos.find((item) => item.id === entry.id);
      const source = String(pano?.name || '').trim();
      if (!pano || source !== entry.source || englishOrReusable(source, english.panoNames[entry.id])) return;
      english.panoNames[entry.id] = translated;
      applied += 1;
      return;
    }

    if (entry.kind === 'folder') {
      const folder = next.sceneFolders.find((item) => item.id === entry.id);
      const source = String(folder?.name || '').trim();
      if (!folder || source !== entry.source || englishOrReusable(source, english.folderNames[entry.id])) return;
      english.folderNames[entry.id] = translated;
      applied += 1;
      return;
    }

    const station = next.stationsByPano[entry.panoId]?.find((item) => item.id === entry.id);
    const source = String(station?.name || '').trim();
    const stationNames = { ...(english.stationNamesByPano[entry.panoId] || {}) };
    if (!station || source !== entry.source || englishOrReusable(source, stationNames[entry.id])) return;
    stationNames[entry.id] = translated;
    english.stationNamesByPano[entry.panoId] = stationNames;
    applied += 1;
  });

  return { document: next, applied };
}

function setEnglishTranslation(document, kind, id, value, panoId = null) {
  const next = cloneDocument(document);
  const name = cleanTranslatedText(value, kind === 'folder' ? 60 : 120);
  const english = next.translations?.en || emptyTranslations().en;
  next.translations = { ...(next.translations || {}), en: english };
  if (kind === 'title') {
    english.title = name;
  } else if (kind === 'pano') {
    english.panoNames = { ...(english.panoNames || {}) };
    if (name) english.panoNames[id] = name;
    else delete english.panoNames[id];
  } else if (kind === 'folder') {
    english.folderNames = { ...(english.folderNames || {}) };
    if (name) english.folderNames[id] = name;
    else delete english.folderNames[id];
  } else if (kind === 'station' && panoId) {
    english.stationNamesByPano = { ...(english.stationNamesByPano || {}) };
    const stationNames = { ...(english.stationNamesByPano[panoId] || {}) };
    if (name) stationNames[id] = name;
    else delete stationNames[id];
    if (Object.keys(stationNames).length) english.stationNamesByPano[panoId] = stationNames;
    else delete english.stationNamesByPano[panoId];
  }
  return next;
}

function releaseRows(payload) {
  const rows = Array.isArray(payload) ? payload : payload?.releases || [];
  return rows.map((release) => ({
    ...release,
    id: release.id || release.releaseId,
    publicId: releasePublicId(release),
    locale: release.locale === 'en' ? 'en' : 'zh-CN',
    note: release.note || '未备注发布',
    sceneCount: Number(release.sceneCount ?? release.scene_count ?? 0),
    createdAt: release.createdAt || release.created_at,
    updatedAt: release.updatedAt || release.updated_at,
    revokedAt: release.revokedAt || release.revoked_at,
    createdByName: release.createdByName || release.created_by_name || '',
  }));
}

function releaseUrl(release) {
  if (release?.publicUrl) return release.publicUrl;
  const publicId = releasePublicId(release);
  return publicId ? new URL(`/t/${encodeURIComponent(publicId)}`, window.location.origin).toString() : '';
}

function sessionNeedsBootstrap(status) {
  if (typeof status?.needsBootstrap === 'boolean') return status.needsBootstrap;
  if (typeof status?.initialized === 'boolean') return !status.initialized;
  if (typeof status?.hasAdmin === 'boolean') return !status.hasAdmin;
  return false;
}

function Shell({ children, className = '' }) {
  return <main className={`cloud-shell ${className}`}>{children}</main>;
}

function Message({ children, kind = 'info' }) {
  if (!children) return null;
  return <div className={`cloud-message ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>{children}</div>;
}

function AuthForm({ bootstrap, bootstrapTokenRequired = false, onSuccess }) {
  const [email, setEmail] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [bootstrapToken, setBootstrapToken] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const user = bootstrap
        ? await cloudApi.bootstrap({ email, password, displayName }, bootstrapToken)
        : await cloudApi.login({ email, password });
      setPassword('');
      setBootstrapToken('');
      onSuccess(user);
    } catch (requestError) {
      setError(requestError.message || '无法完成登录');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell className="cloud-auth">
      <section className="cloud-auth-panel">
        <div className="cloud-eyebrow">VR TOUR CLOUD</div>
        <h1>{bootstrap ? '初始化管理员' : '登录项目空间'}</h1>
        <p>{bootstrap ? '创建首个管理员账号后即可新建和恢复云端项目。' : '使用受邀账号继续编辑云端项目。'}</p>
        <form onSubmit={submit}>
          {bootstrap && (
            <label className="cloud-field">
              <span>显示名称</span>
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required maxLength="80" />
            </label>
          )}
          {bootstrap && bootstrapTokenRequired && (
            <label className="cloud-field">
              <span>初始化口令</span>
              <input type="password" autoComplete="one-time-code" value={bootstrapToken} onChange={(event) => setBootstrapToken(event.target.value)} required maxLength="256" />
            </label>
          )}
          <label className="cloud-field">
            <span>邮箱</span>
            <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label className="cloud-field">
            <span>密码</span>
            <input type="password" autoComplete={bootstrap ? 'new-password' : 'current-password'} value={password} onChange={(event) => setPassword(event.target.value)} required minLength="8" />
          </label>
          <Message kind="error">{error}</Message>
          <button className="cloud-primary" type="submit" disabled={busy}>
            {busy ? '正在提交...' : bootstrap ? '创建管理员' : '登录'}
          </button>
        </form>
      </section>
    </Shell>
  );
}

function CloudEditor() {
  const [session, setSession] = React.useState({ loading: true, user: null, bootstrap: false, bootstrapTokenRequired: false, error: '' });

  const refreshSession = React.useCallback(async () => {
    setSession((current) => ({ ...current, loading: true, error: '' }));
    try {
      const [status, user] = await Promise.all([cloudApi.bootstrapStatus(), cloudApi.currentUser()]);
      setSession({
        loading: false,
        user,
        bootstrap: !user && sessionNeedsBootstrap(status),
        bootstrapTokenRequired: Boolean(status?.bootstrapTokenRequired),
        error: '',
      });
    } catch (error) {
      setSession({ loading: false, user: null, bootstrap: false, bootstrapTokenRequired: false, error: error.message || '无法连接云端服务' });
    }
  }, []);

  React.useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  if (session.loading) {
    return <Shell className="cloud-centered"><div className="cloud-loading">正在连接云端项目空间...</div></Shell>;
  }
  if (session.error) {
    return (
      <Shell className="cloud-centered">
        <section className="cloud-auth-panel">
          <h1>云端服务不可用</h1>
          <Message kind="error">{session.error}</Message>
          <button type="button" className="cloud-primary" onClick={refreshSession}>重新连接</button>
        </section>
      </Shell>
    );
  }
  if (!session.user) {
    return <AuthForm bootstrap={session.bootstrap} bootstrapTokenRequired={session.bootstrapTokenRequired} onSuccess={(user) => setSession({ loading: false, user, bootstrap: false, bootstrapTokenRequired: false, error: '' })} />;
  }
  return <ProjectWorkspace user={session.user} onLogout={refreshSession} />;
}

function ProjectWorkspace({ user, onLogout }) {
  const panoInputRef = React.useRef(null);
  const folderPanoInputRef = React.useRef(null);
  const folderUploadTargetRef = React.useRef(null);
  const entryAudioInputRef = React.useRef(null);
  const stationAudioInputRef = React.useRef(null);
  const audioPlayerRef = React.useRef(null);
  const projectListRef = React.useRef(null);
  const scenePanelRef = React.useRef(null);
  const folderManagerRef = React.useRef(null);
  const projectRef = React.useRef(null);
  const documentRef = React.useRef(emptyDocument());
  const revisionRef = React.useRef(0);
  const pendingSaveRef = React.useRef(null);
  const saveTimerRef = React.useRef(null);
  const saveQueueRef = React.useRef(Promise.resolve());
  const flushSaveRef = React.useRef(() => Promise.resolve());
  const savingRef = React.useRef(false);
  const translationRunRef = React.useRef(0);
  const translationAbortRef = React.useRef(null);
  const translationInFlightRef = React.useRef(false);

  const [projects, setProjects] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [project, setProject] = React.useState(null);
  const [document, setDocument] = React.useState(emptyDocument());
  const [activeSceneId, setActiveSceneId] = React.useState(null);
  const [versions, setVersions] = React.useState([]);
  const [releases, setReleases] = React.useState([]);
  const [showAllProjects, setShowAllProjects] = React.useState(false);
  const [showAllVersions, setShowAllVersions] = React.useState(false);
  const [showAllReleases, setShowAllReleases] = React.useState(false);
  const [releaseHistoryOpen, setReleaseHistoryOpen] = React.useState(false);
  const [editingLocale, setEditingLocale] = React.useState('zh-CN');
  const [autoTranslation, setAutoTranslation] = React.useState({ running: false, completed: 0, total: 0 });
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(initialSidebarCollapsed);
  const [projectLoading, setProjectLoading] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState('等待选择项目');
  const [notice, setNotice] = React.useState('');
  const [error, setError] = React.useState('');
  const [newProjectTitle, setNewProjectTitle] = React.useState('');
  const [creatingProject, setCreatingProject] = React.useState(false);
  const [projectTitleDraft, setProjectTitleDraft] = React.useState('');
  const [renamingProject, setRenamingProject] = React.useState(false);
  const [updatingProject, setUpdatingProject] = React.useState(false);
  const [confirmingProjectDelete, setConfirmingProjectDelete] = React.useState(false);
  const [deletingProject, setDeletingProject] = React.useState(false);
  const [deletingPanoId, setDeletingPanoId] = React.useState(null);
  const [confirmingAllPanosDelete, setConfirmingAllPanosDelete] = React.useState(false);
  const [deletingAllPanos, setDeletingAllPanos] = React.useState(false);
  const [newMemberName, setNewMemberName] = React.useState('');
  const [newMemberEmail, setNewMemberEmail] = React.useState('');
  const [newMemberPassword, setNewMemberPassword] = React.useState('');
  const [creatingMember, setCreatingMember] = React.useState(false);
  const [uploadItems, setUploadItems] = React.useState([]);
  const [pendingAudio, setPendingAudio] = React.useState(null);
  const [placeMode, setPlaceMode] = React.useState(false);
  const [stationName, setStationName] = React.useState('');
  const [targetPanoId, setTargetPanoId] = React.useState('');
  const [restoringVersion, setRestoringVersion] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = React.useState(false);
  const [publishLocale, setPublishLocale] = React.useState('zh-CN');
  const [publishNote, setPublishNote] = React.useState('');
  const [publishedUrl, setPublishedUrl] = React.useState('');
  const [releaseActionId, setReleaseActionId] = React.useState(null);
  const [editingReleaseId, setEditingReleaseId] = React.useState(null);
  const [releaseNoteDraft, setReleaseNoteDraft] = React.useState('');
  const [confirmingRevokeId, setConfirmingRevokeId] = React.useState(null);
  const [sceneQuery, setSceneQuery] = React.useState('');
  const [newFolderName, setNewFolderName] = React.useState('');
  const [expandedFolderId, setExpandedFolderId] = React.useState(null);
  const [folderPickerId, setFolderPickerId] = React.useState(null);
  const [folderPickerQuery, setFolderPickerQuery] = React.useState('');
  const [draggingPanoId, setDraggingPanoId] = React.useState(null);
  const [draggingFolderId, setDraggingFolderId] = React.useState(null);
  const [draggingFolderPano, setDraggingFolderPano] = React.useState(null);
  const [dropLocation, setDropLocation] = React.useState(null);

  const currentPano = document.panos.find((pano) => pano.id === activeSceneId) || document.panos[0] || null;
  const currentStations = currentPano ? document.stationsByPano[currentPano.id] || [] : [];
  const localizedCurrentStations = currentPano
    ? currentStations.map((station) => ({
      ...station,
      name: localizedStationName(document, currentPano.id, station, editingLocale),
    }))
    : [];
  const otherPanos = currentPano ? document.panos.filter((pano) => pano.id !== currentPano.id) : [];
  const displayedVersions = showAllVersions ? versions : versions.slice(0, 5);
  const displayedReleases = showAllReleases ? releases : releases.slice(0, 5);
  const englishIssues = React.useMemo(() => englishTranslationIssues(project, document), [document, project]);
  const pendingAssets = React.useMemo(() => hasPendingAssets(document), [document]);
  const normalizedSceneQuery = sceneQuery.trim().toLocaleLowerCase();
  const visiblePanos = normalizedSceneQuery
    ? document.panos.filter((pano) => localizedPanoName(document, pano, editingLocale).toLocaleLowerCase().includes(normalizedSceneQuery))
    : document.panos;
  const activeFolderPicker = document.sceneFolders.find((folder) => folder.id === folderPickerId) || null;
  const normalizedFolderPickerQuery = folderPickerQuery.trim().toLocaleLowerCase();
  const folderPickerPanos = normalizedFolderPickerQuery
    ? document.panos.filter((pano) => localizedPanoName(document, pano, editingLocale).toLocaleLowerCase().includes(normalizedFolderPickerQuery))
    : document.panos;

  const showDropLocation = React.useCallback((event, kind, targetId, folderId = null) => {
    event.preventDefault();
    const position = pointerDropPosition(event);
    setDropLocation((current) => (
      current?.kind === kind
      && current?.targetId === targetId
      && current?.folderId === folderId
      && current?.position === position
        ? current
        : { kind, targetId, folderId, position }
    ));
    return position;
  }, []);

  const dropLocationClass = React.useCallback((kind, targetId, folderId = null) => (
    dropLocation?.kind === kind
    && dropLocation?.targetId === targetId
    && dropLocation?.folderId === folderId
      ? `drop-${dropLocation.position}`
      : ''
  ), [dropLocation]);

  const finishDrag = React.useCallback(() => {
    setDraggingPanoId(null);
    setDraggingFolderId(null);
    setDraggingFolderPano(null);
    setDropLocation(null);
  }, []);

  const autoScrollScenePanel = React.useCallback((event) => {
    const panel = scenePanelRef.current;
    if (!panel || (!draggingPanoId && !draggingFolderId && !draggingFolderPano)) return;
    const bounds = panel.getBoundingClientRect();
    const edge = Math.min(72, bounds.height / 4);
    if (event.clientY < bounds.top + edge) panel.scrollBy({ top: -24 });
    else if (event.clientY > bounds.bottom - edge) panel.scrollBy({ top: 24 });
  }, [draggingFolderId, draggingFolderPano, draggingPanoId]);

  const stopAudio = React.useCallback(() => {
    const player = audioPlayerRef.current;
    if (player) {
      player.pause();
      player.currentTime = 0;
      audioPlayerRef.current = null;
    }
  }, []);

  const playAudio = React.useCallback((url) => {
    if (!url) return;
    stopAudio();
    const player = new Audio(url);
    audioPlayerRef.current = player;
    player.addEventListener('ended', () => {
      if (audioPlayerRef.current === player) audioPlayerRef.current = null;
    }, { once: true });
    player.play().catch(() => setNotice('语音未能自动播放，可在浏览器中允许媒体播放后重试。'));
  }, [stopAudio]);

  React.useEffect(() => () => {
    stopAudio();
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    translationRunRef.current += 1;
    translationAbortRef.current?.abort();
  }, [stopAudio]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
    } catch {
      // The editor remains usable when storage is disabled by the browser.
    }
  }, [sidebarCollapsed]);

  const applyProject = React.useCallback((raw, options = {}) => {
    const next = normalizeProject(raw, projectRef.current || {});
    if (!next.id) return null;
    if (projectRef.current?.id === next.id && next.revision < revisionRef.current) {
      return projectRef.current;
    }
    if (
      options.skipUnchanged
      && projectRef.current?.id === next.id
      && projectPollingKey(projectRef.current) === projectPollingKey(next)
    ) {
      return projectRef.current;
    }
    projectRef.current = next;
    documentRef.current = next.document;
    revisionRef.current = next.revision;
    setProject(next);
    setDocument(next.document);
    if (options.keepActive) {
      setActiveSceneId((current) => next.document.panos.some((pano) => pano.id === current)
        ? current
        : next.document.currentId || next.document.panos[0]?.id || null);
    } else {
      setActiveSceneId(next.document.currentId || next.document.panos[0]?.id || null);
    }
    if (options.keepActive) {
      setTargetPanoId((current) => (
        next.document.panos.some((pano) => pano.id === current)
          ? current
          : next.document.panos.find((pano) => pano.id !== next.document.currentId)?.id || ''
      ));
    } else {
      setTargetPanoId(next.document.panos.find((pano) => pano.id !== next.document.currentId)?.id || '');
    }
    return next;
  }, []);

  const cancelAutoTranslation = React.useCallback(() => {
    translationRunRef.current += 1;
    translationAbortRef.current?.abort();
    translationAbortRef.current = null;
    translationInFlightRef.current = false;
    setAutoTranslation({ running: false, completed: 0, total: 0 });
  }, []);

  const clearProjectSelection = React.useCallback(() => {
    cancelAutoTranslation();
    const empty = emptyDocument();
    projectRef.current = null;
    documentRef.current = empty;
    revisionRef.current = 0;
    pendingSaveRef.current = null;
    setProject(null);
    setDocument(empty);
    setActiveSceneId(null);
    setTargetPanoId('');
    setVersions([]);
    setReleases([]);
    setShowAllVersions(false);
    setShowAllReleases(false);
    setReleaseHistoryOpen(false);
    setEditingLocale('zh-CN');
    setProjectTitleDraft('');
    setRenamingProject(false);
    setConfirmingProjectDelete(false);
    setDeletingPanoId(null);
    setConfirmingAllPanosDelete(false);
    setDeletingAllPanos(false);
    setPublishedUrl('');
    setPublishDialogOpen(false);
    setPublishNote('');
    setReleaseActionId(null);
    setEditingReleaseId(null);
    setReleaseNoteDraft('');
    setConfirmingRevokeId(null);
    setSceneQuery('');
    setNewFolderName('');
    setExpandedFolderId(null);
    setFolderPickerId(null);
    setFolderPickerQuery('');
    setPendingAudio(null);
    setPlaceMode(false);
    setSaveStatus('请选择或创建项目');
  }, [cancelAutoTranslation]);

  const refreshVersions = React.useCallback(async (projectId) => {
    if (!projectId) return;
    // Version payloads only contain metadata, so load the capped list once and
    // let the sidebar switch between its compact and expanded views locally.
    const rows = versionRows(await cloudApi.listVersions(projectId, 100));
    setVersions(rows);
  }, []);

  const refreshReleases = React.useCallback(async (projectId) => {
    if (!projectId) return [];
    const rows = releaseRows(await cloudApi.listReleases(projectId));
    setReleases(rows);
    return rows;
  }, []);

  const refreshProjects = React.useCallback(async () => {
    const rows = await cloudApi.listProjects();
    setProjects(rows);
    return rows;
  }, []);

  const refreshMembers = React.useCallback(async () => {
    if (user.role !== 'admin') return [];
    const rows = await cloudApi.listUsers();
    setMembers(rows);
    return rows;
  }, [user.role]);

  const selectProject = React.useCallback(async (projectId) => {
    if (!projectId) return;
    cancelAutoTranslation();
    await flushSaveRef.current();
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setProjectLoading(true);
    setError('');
    setNotice('');
    try {
      const raw = await cloudApi.getProject(projectId);
      const next = applyProject(raw);
      if (!next) throw new Error('项目数据无效');
      setShowAllVersions(false);
      setProjectTitleDraft(next.title);
      setRenamingProject(false);
      setConfirmingProjectDelete(false);
      setConfirmingAllPanosDelete(false);
      await Promise.all([refreshVersions(next.id), refreshReleases(next.id), refreshProjects()]);
      setSaveStatus(`云端草稿已加载，版本 ${next.revision}`);
      setPublishedUrl('');
      setSceneQuery('');
      setNewFolderName('');
      setExpandedFolderId(null);
      setFolderPickerId(null);
      setFolderPickerQuery('');
      setPendingAudio(null);
      setPlaceMode(false);
      setReleaseHistoryOpen(false);
      setShowAllReleases(false);
      setEditingReleaseId(null);
      setConfirmingRevokeId(null);
    } catch (requestError) {
      setError(requestError.message || '无法加载项目');
    } finally {
      setProjectLoading(false);
    }
  }, [applyProject, cancelAutoTranslation, refreshProjects, refreshReleases, refreshVersions]);

  React.useEffect(() => {
    let active = true;
    refreshProjects()
      .then((rows) => {
        if (active && rows[0]?.id) return selectProject(rows[0].id);
        return null;
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || '无法读取项目列表');
      });
    return () => {
      active = false;
    };
  }, [refreshProjects, selectProject]);

  React.useEffect(() => {
    refreshMembers().catch((requestError) => {
      setError(requestError.message || '无法读取账号列表');
    });
  }, [refreshMembers]);

  const flushSave = React.useCallback(async ({ throwOnError = false } = {}) => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingSaveRef.current;
    if (!pending) {
      const inFlight = saveQueueRef.current;
      return throwOnError ? inFlight : inFlight.catch(() => undefined);
    }
    pendingSaveRef.current = null;

    const task = async () => {
      savingRef.current = true;
      setSaveStatus('正在保存云端草稿...');
      try {
        const raw = await cloudApi.saveDraft(
          pending.projectId,
          pending.document,
          revisionRef.current,
          pending.label
        );
        const saved = normalizeProject(raw, projectRef.current || { id: pending.projectId });
        if (saved.revision > revisionRef.current) revisionRef.current = saved.revision;
        if (projectRef.current?.id === pending.projectId) {
          projectRef.current = { ...projectRef.current, revision: revisionRef.current, updatedAt: saved.updatedAt || projectRef.current.updatedAt };
          setProject(projectRef.current);
          setSaveStatus(`云端草稿已保存，版本 ${revisionRef.current}`);
        }
      } catch (requestError) {
        if (requestError instanceof CloudApiError && requestError.status === 409) {
          setError(`云端版本已变化（当前版本 ${requestError.details?.currentRevision ?? '未知'}），请重新加载后再保存。`);
        } else {
          setError(requestError.message || '云端草稿保存失败');
        }
        setSaveStatus('云端草稿未保存');
        // Keep the queue rejected so a later manual save can observe an
        // auto-save failure that was already in flight when it was clicked.
        throw requestError;
      } finally {
        savingRef.current = false;
      }
    };
    const queued = saveQueueRef.current.catch(() => undefined).then(task);
    saveQueueRef.current = queued;
    return throwOnError ? queued : queued.catch(() => undefined);
  }, []);

  flushSaveRef.current = flushSave;

  React.useEffect(() => {
    const recovery = () => flushSaveRef.current();
    window.__VR_CLOUD_SAVE_DRAFT__ = recovery;
    return () => {
      if (window.__VR_CLOUD_SAVE_DRAFT__ === recovery) delete window.__VR_CLOUD_SAVE_DRAFT__;
    };
  }, []);

  React.useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      console.error('VR cloud editor unhandled rejection', reason);
      event.preventDefault?.();
      flushSaveRef.current().catch(() => undefined);
      setError(reason?.message || '操作未完成，最近一次云端草稿正在确认保存状态。');
      setSaveStatus('正在确认云端草稿状态...');
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);

  const scheduleSave = React.useCallback((nextDocument, label) => {
    const projectId = projectRef.current?.id;
    if (!projectId) return;
    pendingSaveRef.current = {
      projectId,
      document: serializableDocument(nextDocument),
      label: label || '自动保存',
    };
    setSaveStatus('等待保存云端草稿...');
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      flushSave();
    }, 700);
  }, [flushSave]);

  const updateDocument = React.useCallback((mutate, label) => {
    const next = mutate(cloneDocument(documentRef.current));
    const normalized = normalizeDocument(next);
    documentRef.current = normalized;
    setDocument(normalized);
    scheduleSave(normalized, label);
    return normalized;
  }, [scheduleSave]);

  const createSceneFolder = (event) => {
    event.preventDefault();
    const name = newFolderName.trim();
    if (!name || documentRef.current.sceneFolders.length >= 50) return;
    const folder = { id: createId('folder'), name, panoIds: [] };
    updateDocument((current) => {
      const next = { ...current, sceneFolders: [...current.sceneFolders, folder] };
      return editingLocale === 'en'
        ? setEnglishTranslation(next, 'folder', folder.id, name)
        : next;
    }, `新建发布文件夹：${name}`);
    setNewFolderName('');
    setExpandedFolderId(folder.id);
  };

  const renameSceneFolder = (folderId, value) => {
    const name = String(value || '').trim().slice(0, 60);
    const existing = documentRef.current.sceneFolders.find((folder) => folder.id === folderId);
    const previous = localizedFolderName(documentRef.current, existing, editingLocale, false);
    if (!name || !existing || previous === name) return previous;
    updateDocument((current) => (
      editingLocale === 'en'
        ? setEnglishTranslation(current, 'folder', folderId, name)
        : {
          ...current,
          sceneFolders: current.sceneFolders.map((folder) => (
            folder.id === folderId ? { ...folder, name } : folder
          )),
        }
    ), `${editingLocale === 'en' ? '修改英文文件夹名称' : '重命名发布文件夹'}：${name}`);
    return name;
  };

  const renamePano = (panoId, value) => {
    const name = String(value || '').trim().slice(0, 120);
    const existing = documentRef.current.panos.find((pano) => pano.id === panoId);
    const previous = localizedPanoName(documentRef.current, existing, editingLocale, false);
    if (!name || !existing || previous === name) return previous;
    updateDocument((current) => (
      editingLocale === 'en'
        ? setEnglishTranslation(current, 'pano', panoId, name)
        : {
          ...current,
          panos: current.panos.map((pano) => (
            pano.id === panoId ? { ...pano, name } : pano
          )),
        }
    ), `${editingLocale === 'en' ? '修改英文场景名称' : '重命名全景图'}：${name}`);
    return name;
  };

  const renameStation = (panoId, stationId, value) => {
    const name = String(value || '').trim().slice(0, 120);
    const existing = documentRef.current.stationsByPano[panoId]?.find((station) => station.id === stationId);
    const previous = localizedStationName(documentRef.current, panoId, existing, editingLocale, false);
    if (!name || !existing || previous === name) return previous;
    updateDocument((current) => (
      editingLocale === 'en'
        ? setEnglishTranslation(current, 'station', stationId, name, panoId)
        : {
          ...current,
          stationsByPano: {
            ...current.stationsByPano,
            [panoId]: current.stationsByPano[panoId].map((station) => (
              station.id === stationId ? { ...station, name } : station
            )),
          },
        }
    ), `${editingLocale === 'en' ? '修改英文导航点名称' : '重命名导航点'}：${name}`);
    return name;
  };

  const updateEnglishProjectTitle = (value) => {
    const title = String(value || '').trim().slice(0, 120);
    const previous = localizedProjectTitle(projectRef.current, documentRef.current, 'en', false);
    if (title === previous) return previous;
    updateDocument((current) => setEnglishTranslation(current, 'title', null, title), '修改英文公开标题');
    return title;
  };

  const setProjectEntryPano = (panoId) => {
    const pano = documentRef.current.panos.find((item) => item.id === panoId);
    if (!pano || documentRef.current.currentId === panoId) return;
    updateDocument((current) => ({ ...current, currentId: panoId }), `设置总场景首图：${pano.name}`);
    setNotice(`已将「${pano.name}」设为总场景首图。`);
  };

  const deleteSceneFolder = (folderId) => {
    const folder = documentRef.current.sceneFolders.find((item) => item.id === folderId);
    if (!folder) return;
    updateDocument((current) => ({
      ...current,
      sceneFolders: current.sceneFolders.filter((item) => item.id !== folderId),
    }), `删除发布文件夹：${folder.name}`);
    if (expandedFolderId === folderId) setExpandedFolderId(null);
    if (folderPickerId === folderId) setFolderPickerId(null);
  };

  const setPanoInFolder = (folderId, panoId, included) => {
    updateDocument((current) => ({
      ...current,
      sceneFolders: current.sceneFolders.map((folder) => {
        if (folder.id !== folderId) return folder;
        const exists = folder.panoIds.includes(panoId);
        if (included && !exists) return { ...folder, panoIds: [...folder.panoIds, panoId] };
        if (!included && exists) return { ...folder, panoIds: folder.panoIds.filter((id) => id !== panoId) };
        return folder;
      }),
    }), included ? '添加文件夹场景' : '移除文件夹场景');
  };

  const openPanoForEditing = React.useCallback((panoId) => {
    const currentDocument = documentRef.current;
    const pano = currentDocument.panos.find((item) => item.id === panoId);
    if (!pano?.url) return;
    setActiveSceneId(pano.id);
    setTargetPanoId(currentDocument.panos.find((item) => item.id !== pano.id)?.id || '');
    setPlaceMode(false);
  }, []);

  const reorderPanos = (sourceId, targetId, position) => {
    updateDocument((current) => ({ ...current, panos: moveById(current.panos, sourceId, targetId, position) }), '调整全景顺序');
  };

  const reorderFolders = (sourceId, targetId, position) => {
    updateDocument((current) => ({
      ...current,
      sceneFolders: moveById(current.sceneFolders, sourceId, targetId, position),
    }), '调整发布文件夹顺序');
  };

  const reorderFolderPanos = (folderId, sourcePanoId, targetPanoId, position) => {
    updateDocument((current) => ({
      ...current,
      sceneFolders: current.sceneFolders.map((folder) => (
        folder.id === folderId
          ? { ...folder, panoIds: moveValue(folder.panoIds, sourcePanoId, targetPanoId, position) }
          : folder
      )),
    }), '调整文件夹场景顺序');
  };

  const openFolderUpload = (folderId) => {
    folderUploadTargetRef.current = folderId;
    folderPanoInputRef.current?.click();
  };

  const createProject = async (event) => {
    event.preventDefault();
    const title = newProjectTitle.trim();
    if (!title) return;
    setCreatingProject(true);
    setError('');
    try {
      const created = await cloudApi.createProject(title);
      const rows = await refreshProjects();
      setNewProjectTitle('');
      projectListRef.current?.scrollTo?.({ top: 0 });
      const id = created?.id || rows.find((item) => item.title === title)?.id;
      if (id) await selectProject(id);
    } catch (requestError) {
      setError(requestError.message || '创建项目失败');
    } finally {
      setCreatingProject(false);
    }
  };

  const startProjectRename = () => {
    if (!projectRef.current) return;
    setProjectTitleDraft(projectRef.current.title);
    setConfirmingProjectDelete(false);
    setRenamingProject(true);
  };

  const cancelProjectRename = () => {
    setProjectTitleDraft(projectRef.current?.title || '');
    setRenamingProject(false);
  };

  const renameProject = async (event) => {
    event.preventDefault();
    const activeProject = projectRef.current;
    const title = projectTitleDraft.trim();
    if (!activeProject || !title) return;
    if (title === activeProject.title) {
      setRenamingProject(false);
      return;
    }

    setUpdatingProject(true);
    setError('');
    try {
      await flushSave();
      const raw = await cloudApi.updateProject(activeProject.id, title);
      const renamed = normalizeProject(raw, activeProject);
      const next = {
        ...activeProject,
        title: renamed.title,
        updatedAt: renamed.updatedAt || activeProject.updatedAt,
      };
      projectRef.current = next;
      setProject(next);
      setProjectTitleDraft(next.title);
      setRenamingProject(false);
      await refreshProjects();
      setNotice(`项目已重命名为「${next.title}」。`);
    } catch (requestError) {
      setError(requestError.message || '修改项目名称失败');
    } finally {
      setUpdatingProject(false);
    }
  };

  const deleteProject = async () => {
    const activeProject = projectRef.current;
    if (!activeProject) return;
    setDeletingProject(true);
    setError('');
    try {
      await flushSave();
      await cloudApi.deleteProject(activeProject.id);
      const rows = await refreshProjects();
      projectListRef.current?.scrollTo?.({ top: 0 });
      if (rows[0]?.id) {
        await selectProject(rows[0].id);
      } else {
        clearProjectSelection();
      }
      setNotice(`项目「${activeProject.title}」已删除，公开链接已撤销；已缓存的素材可能短暂仍可访问。`);
    } catch (requestError) {
      setError(requestError.message || '删除项目失败');
    } finally {
      setDeletingProject(false);
      setConfirmingProjectDelete(false);
    }
  };

  const createMember = async (event) => {
    event.preventDefault();
    if (user.role !== 'admin') return;
    setCreatingMember(true);
    setError('');
    try {
      const created = await cloudApi.createEditor({
        displayName: newMemberName,
        email: newMemberEmail,
        password: newMemberPassword,
      });
      setNewMemberName('');
      setNewMemberEmail('');
      setNewMemberPassword('');
      await refreshMembers();
      setNotice(`已创建编辑账号「${created.displayName || created.email}」。`);
    } catch (requestError) {
      setError(requestError.message || '创建编辑账号失败');
    } finally {
      setCreatingMember(false);
    }
  };

  const handleUploadProgress = React.useCallback((next) => {
    setUploadItems((current) => {
      const index = current.findIndex((item) => item.clientId === next.clientId);
      if (index < 0) return [...current, next];
      const updated = [...current];
      updated[index] = { ...updated[index], ...next };
      return updated;
    });
  }, []);

  const addPanoFiles = async (event, targetFolderId = null) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length || !projectRef.current?.id) return;
    const targetFolder = documentRef.current.sceneFolders.find((folder) => folder.id === targetFolderId) || null;
    setError('');
    setNotice(targetFolder
      ? `正在向「${targetFolder.name}」上传 ${files.length} 张全景图...`
      : `正在上传 ${files.length} 张全景图...`);
    try {
      await flushSave({ throwOnError: true });
    } catch (requestError) {
      setError(requestError.message || '当前草稿尚未保存，已取消本次上传。');
      if (folderUploadTargetRef.current === targetFolderId) folderUploadTargetRef.current = null;
      return;
    }
    const uploadedIds = new Set();
    const addUploadedPano = async (item) => {
      const assetId = item?.asset?.id;
      if (!assetId || uploadedIds.has(assetId)) return;
      uploadedIds.add(assetId);
      let next = null;
      if (item.project) {
        const saved = applyProject(item.project, { keepActive: true });
        if (saved) next = saved.document;
      }
      if (!next) {
        next = updateDocument((current) => {
          if (current.panos.some((pano) => pano.assetId === assetId)) return current;
          const pano = {
            id: createId('pano'),
            name: fileLabel(item.fileName),
            assetId,
            status: item.asset?.state || 'queued',
          };
          return {
            ...current,
            panos: [...current.panos, pano],
            currentId: current.currentId || pano.id,
            stationsByPano: { ...current.stationsByPano, [pano.id]: [] },
          };
        }, '上传 1 张全景');
      }
      const uploadedPano = next.panos.find((pano) => pano.assetId === assetId);
      if (targetFolder && uploadedPano) {
        next = updateDocument((current) => ({
          ...current,
          sceneFolders: current.sceneFolders.map((folder) => (
            folder.id === targetFolder.id && !folder.panoIds.includes(uploadedPano.id)
              ? { ...folder, panoIds: [...folder.panoIds, uploadedPano.id] }
              : folder
          )),
        }), `上传并加入文件夹：${targetFolder.name}`);
      }
      setActiveSceneId((current) => current || next.currentId);
      setTargetPanoId((current) => current || next.panos.find((pano) => pano.id !== next.currentId)?.id || '');
      await flushSaveRef.current();
    };
    let results;
    try {
      results = await uploadFiles({
        projectId: projectRef.current.id,
        files,
        kind: 'pano',
        onProgress: handleUploadProgress,
        onAsset: addUploadedPano,
      });
    } catch (requestError) {
      setError(requestError.message || '上传过程意外中断，请稍后重试。');
      setNotice('云端草稿未被覆盖；请检查网络后重新选择文件。');
      if (folderUploadTargetRef.current === targetFolderId) folderUploadTargetRef.current = null;
      return;
    }
    const failed = results.filter((item) => item.error);
    if (targetFolder && uploadedIds.size) {
      try {
        const raw = await cloudApi.getProject(projectRef.current.id);
        const latest = applyProject(raw, { keepActive: true });
        const uploadedPanoIds = new Set(
          latest?.document.panos
            .filter((pano) => uploadedIds.has(pano.assetId))
            .map((pano) => pano.id) || []
        );
        if (uploadedPanoIds.size) {
          updateDocument((current) => ({
            ...current,
            sceneFolders: current.sceneFolders.map((folder) => (
              folder.id === targetFolder.id
                ? { ...folder, panoIds: [...new Set([...folder.panoIds, ...uploadedPanoIds])] }
                : folder
            )),
          }), `确认文件夹上传：${targetFolder.name}`);
          await flushSaveRef.current({ throwOnError: true });
        }
      } catch (requestError) {
        setError(requestError.message || '全景图已上传，但加入文件夹失败，请从素材库重新选择。');
      }
    }
    if (uploadedIds.size) {
      setNotice(targetFolder
        ? `已向「${targetFolder.name}」加入 ${uploadedIds.size} 张素材，正在后台处理预览。`
        : `已接收 ${uploadedIds.size} 张素材，正在后台处理预览。`);
      await refreshVersions(projectRef.current.id);
      await refreshProjects();
    }
    if (failed.length) setError(`${failed.length} 个文件未上传成功，请重新选择后重试。`);
    if (folderUploadTargetRef.current === targetFolderId) folderUploadTargetRef.current = null;
  };

  const uploadAudio = async (event, apply) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !projectRef.current?.id) return;
    setError('');
    let results;
    try {
      results = await uploadFiles({
        projectId: projectRef.current.id,
        files: [file],
        kind: 'audio',
        onProgress: handleUploadProgress,
      });
    } catch (requestError) {
      setError(requestError.message || '音频上传过程意外中断，请稍后重试。');
      setNotice('当前项目草稿未被覆盖。');
      return;
    }
    const result = results[0];
    if (!result?.asset?.id) {
      setError(result?.error?.message || '音频上传失败，请重新选择。');
      return;
    }
    apply(result.asset, result.fileName);
  };

  const setEntryAudio = async (event) => {
    const panoId = activeSceneId;
    if (!panoId) return;
    await uploadAudio(event, (asset, name) => {
      updateDocument((current) => ({
        ...current,
        panos: current.panos.map((pano) => pano.id === panoId
          ? { ...pano, entryAudioAssetId: asset.id, entryAudioName: name, entryAudioUrl: null }
          : pano),
      }), '设置入场语音');
      setNotice(`已接收入场语音「${name}」，完成处理后即可播放。`);
    });
  };

  const setStationAudio = async (event) => {
    await uploadAudio(event, (asset, name) => {
      setPendingAudio({ assetId: asset.id, name });
      setNotice(`已选择站点语音「${name}」。`);
    });
  };

  const beginPlacement = () => {
    if (!currentPano || !targetPanoId || targetPanoId === currentPano.id) {
      setError('请选择不同的目标全景后再放置导航点。');
      return;
    }
    setError('');
    setPlaceMode(true);
    setNotice('请在全景画面中点击导航点位置。');
  };

  const placeStation = React.useCallback(({ yaw, pitch }) => {
    if (!placeMode || !currentPano || !targetPanoId) return;
    const target = documentRef.current.panos.find((pano) => pano.id === targetPanoId);
    const englishTargetName = localizedPanoName(documentRef.current, target, 'en', false) || 'next scene';
    const canonicalName = editingLocale === 'en'
      ? `前往 ${target?.name || '下一站'}`
      : stationName.trim() || `前往 ${target?.name || '下一站'}`;
    const englishName = editingLocale === 'en'
      ? stationName.trim() || `Go to ${englishTargetName}`
      : '';
    const station = {
      id: createId('station'),
      name: canonicalName,
      yaw,
      pitch,
      targetPanoId,
      audioAssetId: pendingAudio?.assetId || null,
      audioName: pendingAudio?.name || null,
    };
    updateDocument((current) => {
      const next = {
        ...current,
        stationsByPano: {
          ...current.stationsByPano,
          [currentPano.id]: [...(current.stationsByPano[currentPano.id] || []), station],
        },
      };
      return englishName
        ? setEnglishTranslation(next, 'station', station.id, englishName, currentPano.id)
        : next;
    }, `添加导航点「${editingLocale === 'en' ? englishName : station.name}」`);
    setPlaceMode(false);
    setPendingAudio(null);
    setStationName('');
    setNotice(`已添加导航点「${editingLocale === 'en' ? englishName : station.name}」。`);
  }, [currentPano, editingLocale, pendingAudio, placeMode, stationName, targetPanoId, updateDocument]);

  const selectStation = React.useCallback((stationId) => {
    const station = (documentRef.current.stationsByPano[activeSceneId] || []).find((item) => item.id === stationId);
    if (!station) return;
    if (station.audioUrl) playAudio(station.audioUrl);
    const target = documentRef.current.panos.find((pano) => pano.id === station.targetPanoId);
    if (!target?.url) {
      setNotice(`目标全景「${target?.name || station.targetPanoId}」仍在处理。`);
      return;
    }
    setActiveSceneId(target.id);
    setTargetPanoId(documentRef.current.panos.find((pano) => pano.id !== target.id)?.id || '');
    setPlaceMode(false);
  }, [activeSceneId, playAudio]);

  const removeStation = (stationId) => {
    if (!currentPano) return;
    updateDocument((current) => ({
      ...current,
      stationsByPano: {
        ...current.stationsByPano,
        [currentPano.id]: (current.stationsByPano[currentPano.id] || []).filter((station) => station.id !== stationId),
      },
    }), '删除导航点');
  };

  const removePano = async (target) => {
    const activeProject = projectRef.current;
    if (!target?.id || !activeProject?.id || deletingPanoId || deletingAllPanos) return;
    const sceneScrollTop = scenePanelRef.current?.scrollTop || 0;
    const panos = documentRef.current.panos;
    const targetIndex = panos.findIndex((pano) => pano.id === target.id);
    const nextActiveId = activeSceneId === target.id
      ? panos[targetIndex + 1]?.id || panos[targetIndex - 1]?.id || null
      : activeSceneId;
    setDeletingPanoId(target.id);
    setError('');
    try {
      await flushSave({ throwOnError: true });
      const raw = await cloudApi.deletePanorama(activeProject.id, target.id, revisionRef.current);
      const updated = applyProject(raw, { keepActive: true });
      if (!updated) throw new Error('删除后的项目数据无效');
      const resolvedActiveId = updated.document.panos.some((pano) => pano.id === nextActiveId)
        ? nextActiveId
        : updated.document.currentId || updated.document.panos[0]?.id || null;
      setActiveSceneId(resolvedActiveId);
      setTargetPanoId(updated.document.panos.find((pano) => pano.id !== resolvedActiveId)?.id || '');
      setPlaceMode(false);
      setPendingAudio(null);
      await Promise.all([refreshProjects(), refreshVersions(updated.id)]);
      if (raw.sourceReclaimed) {
        setNotice(`已删除全景「${target.name}」，原图与派生预览正在后台清理。`);
      } else if (raw.sourceRetainedReason) {
        setNotice(`已从当前编辑器移除全景「${target.name}」；因历史版本或已发布链接仍在引用，原图已保留。`);
      } else {
        setNotice(`已从当前编辑器移除全景「${target.name}」。`);
      }
    } catch (requestError) {
      if (requestError instanceof CloudApiError && requestError.status === 409) {
        setError('项目已被更新，请先重新读取后再删除。');
      } else {
        setError(requestError.message || '删除全景图失败');
      }
    } finally {
      setDeletingPanoId(null);
      const restoreScroll = () => {
        if (scenePanelRef.current) scenePanelRef.current.scrollTop = sceneScrollTop;
      };
      if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(restoreScroll);
      else restoreScroll();
    }
  };

  const deleteAllPanos = async () => {
    const activeProject = projectRef.current;
    if (!activeProject?.id || deletingAllPanos || documentRef.current.panos.length === 0) return;
    setDeletingAllPanos(true);
    setError('');
    try {
      await flushSave({ throwOnError: true });
      const raw = await cloudApi.deleteAllPanoramas(activeProject.id, revisionRef.current, true);
      const updated = applyProject(raw);
      if (!updated) throw new Error('清空后的项目数据无效');
      setPlaceMode(false);
      setPendingAudio(null);
      await Promise.all([refreshProjects(), refreshVersions(updated.id)]);
      const retained = raw.sourceRetainedCount > 0
        ? `；${raw.sourceRetainedCount} 份素材因历史版本或公开链接仍被保留`
        : '';
      setNotice(`已删除全部 ${raw.removedCount} 张全景图${retained}。`);
    } catch (requestError) {
      if (requestError instanceof CloudApiError && requestError.status === 409) {
        setError('项目已被更新，请先重新读取后再删除。');
      } else {
        setError(requestError.message || '删除全部全景图失败');
      }
    } finally {
      setDeletingAllPanos(false);
      setConfirmingAllPanosDelete(false);
    }
  };

  React.useEffect(() => {
    if (!confirmingAllPanosDelete) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !deletingAllPanos) setConfirmingAllPanosDelete(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [confirmingAllPanosDelete, deletingAllPanos]);

  const restoreVersion = async (version) => {
    if (!projectRef.current?.id || !version?.id) return;
    cancelAutoTranslation();
    setRestoringVersion(true);
    setError('');
    try {
      await flushSave();
      const raw = await cloudApi.restoreVersion(projectRef.current.id, version.id);
      const restored = applyProject(raw);
      if (!restored) throw new Error('恢复后的项目数据无效');
      await Promise.all([refreshVersions(restored.id), refreshProjects()]);
      setNotice(`已恢复版本「${version.label}」。`);
    } catch (requestError) {
      setError(requestError.message || '恢复版本失败');
    } finally {
      setRestoringVersion(false);
    }
  };

  const saveManualVersion = async () => {
    if (!projectRef.current?.id) return;
    setError('');
    try {
      // Flush the rolling draft first, then always create an explicit restore
      // point. A pending auto-save must never consume the user's manual save.
      await flushSave({ throwOnError: true });
      await cloudApi.createVersion(projectRef.current.id, '手动保存', revisionRef.current);
      await refreshVersions(projectRef.current.id);
      setNotice('已记录云端版本。');
    } catch (requestError) {
      setError(requestError.message || '创建版本失败');
    }
  };

  const toggleVersions = () => {
    setShowAllVersions((expanded) => !expanded);
  };

  const refreshActiveProject = async () => {
    if (!projectRef.current?.id) return;
    cancelAutoTranslation();
    setError('');
    try {
      await flushSave();
      const raw = await cloudApi.getProject(projectRef.current.id);
      applyProject(raw, { keepActive: true });
      await Promise.all([refreshVersions(projectRef.current.id), refreshReleases(projectRef.current.id)]);
      setNotice('已重新读取云端草稿。');
    } catch (requestError) {
      setError(requestError.message || '重新读取失败');
    }
  };

  const openPublishDialog = () => {
    if (!projectRef.current?.id) return;
    setPublishLocale(editingLocale);
    setPublishNote('');
    setPublishDialogOpen(true);
    setError('');
  };

  const autoFillEnglishTranslations = React.useCallback(async () => {
    const activeProject = projectRef.current;
    if (!activeProject?.id || translationInFlightRef.current) return;
    const entries = missingEnglishTranslationEntries(activeProject, documentRef.current);
    if (entries.length === 0) {
      setNotice('英文名称已全部补全。');
      return;
    }

    const total = new Set(entries.map((entry) => entry.source)).size;
    const runId = translationRunRef.current + 1;
    translationRunRef.current = runId;
    translationAbortRef.current?.abort();
    const controller = new AbortController();
    translationAbortRef.current = controller;
    translationInFlightRef.current = true;
    setAutoTranslation({ running: true, completed: 0, total });
    setError('');
    setNotice(`正在通过公共翻译服务处理 ${total} 个不同名称...`);

    try {
      const result = await autoTranslateTexts(entries.map((entry) => entry.source), {
        signal: controller.signal,
        onProgress: ({ completed, total: progressTotal }) => {
          if (translationRunRef.current === runId) {
            setAutoTranslation({ running: true, completed, total: progressTotal });
          }
        },
      });
      if (translationRunRef.current !== runId || projectRef.current?.id !== activeProject.id) return;

      const filled = applyAutoEnglishTranslations(
        projectRef.current,
        documentRef.current,
        entries,
        result.translations
      );
      if (filled.applied > 0) {
        updateDocument(() => filled.document, '自动补全英文名称');
      }

      const remaining = englishTranslationIssues(projectRef.current, filled.document).length;
      if (filled.applied === 0 && remaining > 0) {
        setError('本次没有取得可用的英文结果，请稍后点击“自动补全英文”重试，或手动填写。');
        setNotice('');
      } else if (remaining > 0) {
        setNotice(`已自动补全 ${filled.applied} 项，仍有 ${remaining} 项未完成，可再次重试或手动修改。`);
      } else {
        setNotice(`已自动补全 ${filled.applied} 项英文名称，并写入同一个云端草稿。`);
      }
    } catch (requestError) {
      if (requestError?.name !== 'AbortError' && translationRunRef.current === runId) {
        setError(requestError.message || '自动翻译失败，请稍后重试或手动填写。');
        setNotice('');
      }
    } finally {
      if (translationRunRef.current === runId) {
        translationInFlightRef.current = false;
        translationAbortRef.current = null;
        setAutoTranslation((current) => ({ ...current, running: false }));
      }
    }
  }, [updateDocument]);

  const switchEditingLocale = (locale) => {
    setEditingLocale(locale);
    setStationName('');
    setNewFolderName('');
    setSceneQuery('');
    setFolderPickerQuery('');
    if (locale === 'en' && missingEnglishTranslationEntries(projectRef.current, documentRef.current).length > 0) {
      void autoFillEnglishTranslations();
    }
  };

  const publish = async () => {
    if (!projectRef.current?.id) return;
    const note = publishNote.trim();
    if (!note) {
      setError('请填写发布备注。');
      return;
    }
    if (publishLocale === 'en' && englishIssues.length > 0) {
      setError(`英文内容还有 ${englishIssues.length} 项未填写，暂不能发布。`);
      return;
    }
    setPublishing(true);
    setError('');
    try {
      await flushSave({ throwOnError: true });
      const release = await cloudApi.createRelease(projectRef.current.id, { locale: publishLocale, note });
      const publicId = releasePublicId(release);
      if (!publicId) throw new Error('服务端没有返回公开链接');
      const url = releaseUrl(release);
      setPublishedUrl(url);
      setPublishDialogOpen(false);
      setPublishNote('');
      await refreshReleases(projectRef.current.id);
      setNotice(`已发布${publishLocale === 'en' ? '英文' : '中文'}固定公开版本。`);
    } catch (requestError) {
      setError(requestError.message || '发布失败');
      if (requestError instanceof CloudApiError && requestError.status === 422 && projectRef.current?.id) {
        try {
          const raw = await cloudApi.getProject(projectRef.current.id);
          applyProject(raw, { keepActive: true });
        } catch {
          // The publish error remains the actionable message if the status refresh also fails.
        }
      }
    } finally {
      setPublishing(false);
    }
  };

  const saveReleaseNote = async (releaseId) => {
    const note = releaseNoteDraft.trim();
    if (!projectRef.current?.id || !releaseId || !note) return;
    setReleaseActionId(releaseId);
    setError('');
    try {
      await cloudApi.updateReleaseNote(projectRef.current.id, releaseId, note);
      await refreshReleases(projectRef.current.id);
      setEditingReleaseId(null);
      setReleaseNoteDraft('');
      setNotice('发布备注已更新。');
    } catch (requestError) {
      setError(requestError.message || '更新发布备注失败');
    } finally {
      setReleaseActionId(null);
    }
  };

  const revokeRelease = async (releaseId) => {
    if (!projectRef.current?.id || !releaseId) return;
    setReleaseActionId(releaseId);
    setError('');
    try {
      await cloudApi.revokeRelease(projectRef.current.id, releaseId);
      const revoked = releases.find((release) => release.id === releaseId);
      if (revoked && publishedUrl === releaseUrl(revoked)) setPublishedUrl('');
      await refreshReleases(projectRef.current.id);
      setConfirmingRevokeId(null);
      setNotice('该公开链接已撤销，其他发布链接不受影响。');
    } catch (requestError) {
      setError(requestError.message || '撤销公开链接失败');
    } finally {
      setReleaseActionId(null);
    }
  };

  const copyReleaseLink = async (release) => {
    const url = releaseUrl(release);
    if (!url) return;
    try {
      await window.navigator.clipboard.writeText(url);
      setNotice('公开链接已复制。');
    } catch {
      setError('浏览器未允许复制，请打开链接后从地址栏复制。');
    }
  };

  const pollPendingAssets = React.useCallback(async () => {
    const projectId = projectRef.current?.id;
    if (!projectId || savingRef.current || pendingSaveRef.current) return;
    try {
      const raw = await cloudApi.getProject(projectId);
      if (projectRef.current?.id === projectId) {
        applyProject(raw, { keepActive: true, skipUnchanged: true });
      }
    } catch {
      // A later user action exposes request failures; polling should not displace the editor.
    }
  }, [applyProject]);

  useAssetStatusPolling({
    projectId: project?.id,
    enabled: pendingAssets,
    intervalMs: getCloudConfig().pollIntervalMs,
    onPoll: pollPendingAssets,
  });

  const logout = async () => {
    try {
      await flushSave();
      await cloudApi.logout();
    } finally {
      onLogout();
    }
  };

  return (
    <Shell className={`cloud-editor ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className={`cloud-sidebar ${sidebarCollapsed ? 'is-collapsed' : ''}`} aria-label="项目侧栏">
        {sidebarCollapsed ? (
          <button type="button" className="cloud-icon-button cloud-sidebar-toggle" aria-label="展开项目侧栏" title="展开项目侧栏" aria-expanded="false" onClick={() => setSidebarCollapsed(false)}>›</button>
        ) : (
          <>
            <div className="cloud-brand-row">
              <div>
                <div className="cloud-eyebrow">VR TOUR CLOUD</div>
                <h1>云端导览编辑器</h1>
              </div>
              <div className="cloud-brand-actions">
                <button type="button" className="cloud-icon-button cloud-sidebar-toggle" aria-label="收起项目侧栏" title="收起项目侧栏" aria-expanded="true" onClick={() => setSidebarCollapsed(true)}>‹</button>
                <button type="button" className="cloud-text-button" onClick={logout}>退出</button>
              </div>
            </div>
            <div className="cloud-user">{user.displayName || user.display_name || user.email}</div>

            <section className="cloud-section cloud-projects-section">
              <div className="cloud-section-heading"><span>项目</span><span>{projects.length}</span></div>
              <div ref={projectListRef} className={`cloud-project-list ${showAllProjects ? 'is-expanded' : ''}`} aria-label="项目列表">
                {projects.map((item) => (
                  <button key={item.id} type="button" className={`cloud-project-row ${project?.id === item.id ? 'active' : ''}`} onClick={() => selectProject(item.id)}>
                    <span>{item.title}</span>
                    <small>{dateLabel(item.updatedAt || item.updated_at)}</small>
                  </button>
                ))}
              </div>
              {projects.length > 5 && (
                <button type="button" className="cloud-text-button cloud-more" onClick={() => setShowAllProjects((current) => !current)}>
                  {showAllProjects ? '收起项目' : '查看更多项目'}
                </button>
              )}
              <form className="cloud-create-project" onSubmit={createProject}>
                <div className="cloud-project-form-row">
                  <input value={newProjectTitle} onChange={(event) => setNewProjectTitle(event.target.value)} placeholder="新项目名称" aria-label="新项目名称" maxLength="120" required />
                  <button type="submit" className="cloud-secondary" disabled={creatingProject}>{creatingProject ? '创建中...' : '新建项目'}</button>
                </div>
              </form>
              {project && (
                <div className="cloud-project-management">
                  {renamingProject ? (
                    <form className="cloud-project-rename-form" onSubmit={renameProject}>
                      <input value={projectTitleDraft} onChange={(event) => setProjectTitleDraft(event.target.value)} aria-label="项目名称" maxLength="120" required autoFocus />
                      <div className="cloud-project-action-row">
                        <button type="submit" className="cloud-secondary compact" disabled={updatingProject}>{updatingProject ? '保存中...' : '保存名称'}</button>
                        <button type="button" className="cloud-text-button" onClick={cancelProjectRename} disabled={updatingProject}>取消</button>
                      </div>
                    </form>
                  ) : (
                    <div className="cloud-project-active-row">
                      <span title={project.title}>{project.title}</span>
                      <div className="cloud-project-action-row">
                        <button type="button" className="cloud-secondary compact" onClick={startProjectRename} disabled={projectLoading || deletingProject}>重命名</button>
                        <button type="button" className="cloud-danger-button compact" onClick={() => setConfirmingProjectDelete(true)} disabled={projectLoading || deletingProject}>删除项目</button>
                      </div>
                    </div>
                  )}
                  {confirmingProjectDelete && (
                    <div className="cloud-project-delete-confirm" role="alertdialog" aria-label="确认删除项目">
                      <p>删除后项目会从编辑列表移除，并撤销已发布的公开链接；已缓存的素材可能短暂仍可访问。</p>
                      <div className="cloud-project-action-row">
                        <button type="button" className="cloud-secondary compact" onClick={() => setConfirmingProjectDelete(false)} disabled={deletingProject}>取消</button>
                        <button type="button" className="cloud-danger-button compact" onClick={deleteProject} disabled={deletingProject}>{deletingProject ? '删除中...' : '确认删除'}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="cloud-section">
              <div className="cloud-section-heading"><span>云端版本</span><span>{versions.length}</span></div>
              {versions.length === 0 ? <p className="cloud-empty">暂无手动保存版本。</p> : (
                <>
                  <div className="cloud-version-list">
                    {displayedVersions.map((version) => (
                      <div key={version.id} className="cloud-version-row">
                        <div><strong>{version.label}</strong><small>{dateLabel(version.createdAt)}</small></div>
                        <button type="button" className="cloud-secondary compact" disabled={restoringVersion || projectLoading} onClick={() => restoreVersion(version)}>恢复</button>
                      </div>
                    ))}
                  </div>
                  {versions.length > 5 && <button type="button" className="cloud-text-button cloud-more" onClick={toggleVersions}>{showAllVersions ? '收起版本' : '查看更多版本'}</button>}
                </>
              )}
            </section>

            {user.role === 'admin' && (
              <section className="cloud-section">
                <div className="cloud-section-heading"><span>编辑账号</span><span>{members.length}</span></div>
                <form className="cloud-create-project" onSubmit={createMember}>
                  <input value={newMemberName} onChange={(event) => setNewMemberName(event.target.value)} placeholder="显示名称" maxLength="80" required />
                  <input type="email" value={newMemberEmail} onChange={(event) => setNewMemberEmail(event.target.value)} placeholder="邮箱" required />
                  <input type="password" value={newMemberPassword} onChange={(event) => setNewMemberPassword(event.target.value)} placeholder="初始密码" minLength="8" required />
                  <button type="submit" className="cloud-secondary" disabled={creatingMember}>{creatingMember ? '创建中...' : '创建编辑账号'}</button>
                </form>
              </section>
            )}
          </>
        )}
      </aside>

      <section className="cloud-workspace">
        <header className="cloud-toolbar">
          <div className="cloud-toolbar-context">
            <div className="cloud-project-title">{project?.title || '请选择或创建项目'}</div>
            <div className="cloud-save-status">{projectLoading ? '正在加载项目...' : saveStatus}</div>
            {project && (
              <div className="cloud-language-editor">
                <span>编辑内容</span>
                <div className="cloud-segmented" role="group" aria-label="编辑内容语言">
                  <button type="button" className={editingLocale === 'zh-CN' ? 'active' : ''} aria-pressed={editingLocale === 'zh-CN'} onClick={() => switchEditingLocale('zh-CN')}>中文</button>
                  <button type="button" className={editingLocale === 'en' ? 'active' : ''} aria-pressed={editingLocale === 'en'} onClick={() => switchEditingLocale('en')}>English</button>
                </div>
                {editingLocale === 'en' && (
                  <>
                    <button
                      type="button"
                      className="cloud-secondary cloud-auto-translate-button"
                      title="仅将待翻译的名称文字发送到公共翻译服务"
                      disabled={autoTranslation.running || englishIssues.length === 0}
                      onClick={autoFillEnglishTranslations}
                    >
                      {autoTranslation.running
                        ? `翻译 ${autoTranslation.completed}/${autoTranslation.total}`
                        : englishIssues.length > 0
                          ? `自动补全英文（${englishIssues.length}）`
                          : '英文已补全'}
                    </button>
                    <label className="cloud-english-title">
                      <span>英文公开标题</span>
                      <input
                        key={`${project.id}-${document.translations.en.title}`}
                        defaultValue={document.translations.en.title}
                        placeholder={CJK_PATTERN.test(project.title) ? '输入英文标题' : project.title}
                        maxLength="120"
                        onBlur={(event) => { event.currentTarget.value = updateEnglishProjectTitle(event.currentTarget.value); }}
                        onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                      />
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="cloud-toolbar-actions">
            <button type="button" className="cloud-secondary" onClick={refreshActiveProject} disabled={!project || projectLoading}>重新读取</button>
            <button type="button" className="cloud-secondary" onClick={saveManualVersion} disabled={!project || projectLoading}>保存版本</button>
            <button type="button" className="cloud-secondary" onClick={() => setReleaseHistoryOpen(true)} disabled={!project}>已发布链接 {releases.length}</button>
            <button type="button" className="cloud-primary" onClick={openPublishDialog} disabled={!project || publishing}>发布{editingLocale === 'en' ? '英文' : '中文'}链接</button>
          </div>
        </header>

        <div className="cloud-notices">
          <Message>{notice}</Message>
          <Message kind="error">{error}</Message>
          {publishedUrl && <div className="cloud-published"><span>公开链接</span><a href={publishedUrl} target="_blank" rel="noreferrer">{publishedUrl}</a></div>}
        </div>

        {!project ? (
          <div className="cloud-empty-state">先在左侧创建项目，再上传全景素材。</div>
        ) : (
          <div className="cloud-editor-grid">
            <section ref={scenePanelRef} className="cloud-panel cloud-scenes-panel" onDragOver={autoScrollScenePanel}>
              <div className="cloud-panel-header">
                <div><h2>场景</h2><span>{document.panos.length} 个</span></div>
                <div className="cloud-panel-actions">
                  <button type="button" className="cloud-secondary compact" onClick={() => folderManagerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>文件夹</button>
                  <button type="button" className="cloud-danger-button compact" disabled={document.panos.length === 0 || Boolean(deletingPanoId) || deletingAllPanos} onClick={() => setConfirmingAllPanosDelete(true)}>删除全部</button>
                  <button type="button" className="cloud-primary" disabled={deletingAllPanos} onClick={() => panoInputRef.current?.click()}>上传全景图</button>
                </div>
                <input ref={panoInputRef} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/bmp,.jpg,.jpeg,.png,.webp,.gif,.avif,.bmp" onChange={addPanoFiles} />
                <input ref={folderPanoInputRef} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/bmp,.jpg,.jpeg,.png,.webp,.gif,.avif,.bmp" onChange={(event) => addPanoFiles(event, folderUploadTargetRef.current)} />
              </div>
              {uploadItems.length > 0 && <div className="cloud-upload-list">
                {uploadItems.slice(-12).map((item) => <div className={`cloud-upload-row ${item.state}`} key={item.clientId}>
                  <span>{item.name}</span><small>{item.state === 'uploading' ? `${percent(item.loaded, item.total)}%` : item.error || assetStatus(item.state)}</small>
                </div>)}
              </div>}
              {document.panos.length > 0 && <label className="cloud-scene-search"><span>查找全景图</span><input type="search" value={sceneQuery} onChange={(event) => setSceneQuery(event.target.value)} placeholder="输入名称或编号" /></label>}
              <div className="cloud-scene-list">
                {visiblePanos.map((pano) => (
                  <div
                    key={pano.id}
                    className={`cloud-scene-row ${pano.id === activeSceneId ? 'active' : ''} ${draggingPanoId === pano.id && !draggingFolderPano ? 'is-dragging' : ''} ${dropLocationClass('pano', pano.id)}`}
                    onDragOver={(event) => {
                      if (draggingPanoId && !draggingFolderPano && draggingPanoId !== pano.id) {
                        showDropLocation(event, 'pano', pano.id);
                      }
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (draggingPanoId && !draggingFolderPano) {
                        reorderPanos(draggingPanoId, pano.id, pointerDropPosition(event));
                      }
                      finishDrag();
                    }}
                  >
                    <button type="button" className="cloud-drag-handle" draggable onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', pano.id); setDraggingPanoId(pano.id); setDraggingFolderId(null); setDraggingFolderPano(null); setDropLocation(null); }} onDragEnd={finishDrag} aria-label={`拖动全景图 ${localizedPanoName(document, pano, editingLocale)}`} title="拖动调整顺序">⋮⋮</button>
                    <div className="cloud-scene-main">
                      <button type="button" className="cloud-scene-preview" disabled={!pano.url || Boolean(deletingPanoId) || deletingAllPanos} onClick={() => openPanoForEditing(pano.id)} aria-label={`编辑全景图 ${localizedPanoName(document, pano, editingLocale)}`} title="进入场景编辑">
                        {pano.thumbnailUrl ? <img src={pano.thumbnailUrl} alt="" loading="lazy" /> : <span className="cloud-thumbnail-placeholder" />}
                      </button>
                      <span>
                        <input
                          key={`${editingLocale}-${localizedPanoName(document, pano, editingLocale, false)}`}
                          className="cloud-scene-name-input"
                          aria-label={`${editingLocale === 'en' ? '英文' : ''}全景图名称 ${pano.name}`}
                          title="修改文件名"
                          defaultValue={localizedPanoName(document, pano, editingLocale, false)}
                          placeholder={editingLocale === 'en' ? `英文名称（中文：${pano.name}）` : ''}
                          maxLength="120"
                          disabled={Boolean(deletingPanoId) || deletingAllPanos}
                          onBlur={(event) => {
                            const saved = renamePano(pano.id, event.currentTarget.value);
                            event.currentTarget.value = saved;
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                              event.preventDefault();
                              const saved = renamePano(pano.id, event.currentTarget.value);
                              event.currentTarget.value = saved;
                              event.currentTarget.blur();
                            }
                            if (event.key === 'Escape') {
                              event.currentTarget.value = localizedPanoName(documentRef.current, pano, editingLocale, false);
                              event.currentTarget.blur();
                            }
                          }}
                        />
                        <small>{assetStatus(pano.status, pano.tileState)}</small>
                      </span>
                    </div>
                    {pano.id === document.currentId ? (
                      <span className="cloud-entry-badge" aria-label={`总场景首图 ${localizedPanoName(document, pano, editingLocale)}`} title="公开链接首次打开时显示此场景">首图</span>
                    ) : (
                      <button type="button" className="cloud-secondary compact cloud-entry-action" aria-label={`将 ${localizedPanoName(document, pano, editingLocale)} 设为总场景首图`} title="设为公开链接首次打开的场景" disabled={Boolean(deletingPanoId) || deletingAllPanos} onClick={() => setProjectEntryPano(pano.id)}>设为首图</button>
                    )}
                    <button type="button" className="cloud-icon-button" aria-label={`删除全景图 ${localizedPanoName(document, pano, editingLocale)}`} title="直接删除全景图" disabled={Boolean(deletingPanoId) || deletingAllPanos} onClick={() => removePano(pano)}>{deletingPanoId === pano.id ? '…' : '×'}</button>
                  </div>
                ))}
                {document.panos.length === 0 && <p className="cloud-empty">上传完成后，服务端会生成缩略图和编辑预览。</p>}
                {document.panos.length > 0 && visiblePanos.length === 0 && <p className="cloud-empty">没有符合条件的全景图。</p>}
              </div>

              <section ref={folderManagerRef} className="cloud-folder-manager" aria-labelledby="cloud-folder-heading">
                <div className="cloud-folder-heading"><div><h2 id="cloud-folder-heading">发布文件夹</h2><span>{document.sceneFolders.length} 个</span></div><small>公开页只显示这里创建的文件夹</small></div>
                <form className="cloud-folder-create" onSubmit={createSceneFolder}>
                  <input aria-label="新文件夹名称" value={newFolderName} onChange={(event) => setNewFolderName(event.target.value)} maxLength="60" placeholder={editingLocale === 'en' ? 'e.g. First Floor' : '例如：一楼'} />
                  <button type="submit" className="cloud-primary" disabled={!newFolderName.trim() || document.sceneFolders.length >= 50}>新建</button>
                </form>
                <div className="cloud-folder-list">
                  {document.sceneFolders.map((folder) => {
                    const folderPanos = folder.panoIds.map((panoId) => document.panos.find((pano) => pano.id === panoId)).filter(Boolean);
                    const expanded = expandedFolderId === folder.id;
                    const folderName = localizedFolderName(document, folder, editingLocale);
                    const folderNameValue = localizedFolderName(document, folder, editingLocale, false);
                    const entryName = folderPanos[0] ? localizedPanoName(document, folderPanos[0], editingLocale) : '';
                    return (
                      <article
                        key={folder.id}
                        className={`cloud-folder ${expanded ? 'is-expanded' : ''} ${draggingFolderId === folder.id ? 'is-dragging' : ''} ${dropLocationClass('folder', folder.id)} ${dropLocation?.kind === 'folder-add' && dropLocation.targetId === folder.id ? 'is-drop-target' : ''}`}
                        onDragOver={(event) => {
                          if (draggingFolderId && draggingFolderId !== folder.id) {
                            showDropLocation(event, 'folder', folder.id);
                          } else if (draggingPanoId && draggingFolderPano?.folderId !== folder.id) {
                            event.preventDefault();
                            setDropLocation({ kind: 'folder-add', targetId: folder.id, folderId: null, position: null });
                          }
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (draggingFolderId) reorderFolders(draggingFolderId, folder.id, pointerDropPosition(event));
                          else if (draggingPanoId && draggingFolderPano?.folderId !== folder.id) setPanoInFolder(folder.id, draggingPanoId, true);
                          finishDrag();
                        }}
                      >
                        <div className="cloud-folder-header">
                          <button type="button" className="cloud-drag-handle" draggable onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', folder.id); setDraggingFolderId(folder.id); setDraggingPanoId(null); setDraggingFolderPano(null); setDropLocation(null); }} onDragEnd={finishDrag} aria-label={`拖动文件夹 ${folderName}`} title="拖动调整文件夹顺序">⋮⋮</button>
                          <button type="button" className="cloud-folder-toggle" aria-expanded={expanded} onClick={() => setExpandedFolderId(expanded ? null : folder.id)}>
                            <span className="cloud-folder-shape" aria-hidden="true" />
                            <span><strong>{folderName}</strong><small>{folderPanos.length ? `${folderPanos.length} 张 · 入口 ${entryName}` : '尚未添加全景图'}</small></span>
                          </button>
                          <button type="button" className="cloud-icon-button" onClick={() => deleteSceneFolder(folder.id)} aria-label={`删除文件夹 ${folderName}`} title="只删除文件夹，不删除全景图">×</button>
                        </div>
                        {expanded && <div className="cloud-folder-body">
                          <label className="cloud-folder-name"><span>{editingLocale === 'en' ? '英文文件夹名称' : '文件夹名称'}</span><input key={`${editingLocale}-${folderNameValue}`} defaultValue={folderNameValue} placeholder={editingLocale === 'en' ? `英文名称（中文：${folder.name}）` : ''} maxLength="60" onBlur={(event) => { const saved = renameSceneFolder(folder.id, event.currentTarget.value); event.currentTarget.value = saved; }} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') { event.currentTarget.value = localizedFolderName(documentRef.current, folder, editingLocale, false); event.currentTarget.blur(); } }} /></label>
                          <div className="cloud-folder-actions">
                            <button type="button" className="cloud-secondary" onClick={() => { setFolderPickerId(folder.id); setFolderPickerQuery(''); }}>选择已上传</button>
                            <button type="button" className="cloud-secondary" onClick={() => openFolderUpload(folder.id)}>上传新全景</button>
                          </div>
                          <div className="cloud-folder-scenes">
                            {folderPanos.map((pano, index) => (
                              <div
                                className={`cloud-folder-scene ${pano.id === activeSceneId ? 'active' : ''} ${draggingFolderPano?.folderId === folder.id && draggingFolderPano?.panoId === pano.id ? 'is-dragging' : ''} ${dropLocationClass('folder-pano', pano.id, folder.id)}`}
                                key={pano.id}
                                onDragOver={(event) => {
                                  if (draggingFolderPano?.folderId === folder.id && draggingFolderPano.panoId !== pano.id) {
                                    showDropLocation(event, 'folder-pano', pano.id, folder.id);
                                  }
                                }}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (draggingFolderPano?.folderId === folder.id) {
                                    reorderFolderPanos(folder.id, draggingFolderPano.panoId, pano.id, pointerDropPosition(event));
                                  }
                                  finishDrag();
                                }}
                              >
                                <button type="button" className="cloud-drag-handle" draggable onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', pano.id); setDraggingFolderPano({ folderId: folder.id, panoId: pano.id }); setDraggingPanoId(pano.id); setDraggingFolderId(null); setDropLocation(null); }} onDragEnd={finishDrag} aria-label={`拖动 ${folderName} 中的 ${localizedPanoName(document, pano, editingLocale)}`} title="拖动调整文件夹内顺序">⋮⋮</button>
                                <button type="button" className="cloud-folder-scene-main" disabled={!pano.url || Boolean(deletingPanoId) || deletingAllPanos} aria-label={`编辑全景图 ${localizedPanoName(document, pano, editingLocale)}`} aria-current={pano.id === activeSceneId ? 'true' : undefined} onClick={() => openPanoForEditing(pano.id)}>
                                  {pano.thumbnailUrl ? <img src={pano.thumbnailUrl} alt="" loading="lazy" /> : <span className="cloud-folder-thumbnail-placeholder" />}
                                  <span className="cloud-folder-scene-name"><strong>{localizedPanoName(document, pano, editingLocale)}</strong>{index === 0 && <small>入口图</small>}</span>
                                </button>
                                <button type="button" className="cloud-icon-button" onClick={() => setPanoInFolder(folder.id, pano.id, false)} aria-label={`从 ${folderName} 移除 ${localizedPanoName(document, pano, editingLocale)}`} title="从文件夹移除，不删除全景图">×</button>
                              </div>
                            ))}
                            {folderPanos.length === 0 && <p className="cloud-empty">从素材库选择，或直接上传新的全景图。</p>}
                          </div>
                        </div>}
                      </article>
                    );
                  })}
                  {document.sceneFolders.length === 0 && <p className="cloud-empty">新建“一楼、二楼”等文件夹，发布后访客点击即进入该文件夹的入口图。</p>}
                </div>
              </section>
            </section>

            <section className="cloud-viewer-panel">
              {currentPano?.url ? (
                <PanoramaViewer
                  src={currentPano.url}
                  stations={localizedCurrentStations}
                  onPlace={placeStation}
                  onSelectStation={selectStation}
                  onError={(viewerError) => setError(viewerError?.message || '全景预览加载失败')}
                  onContextLost={() => setNotice('显卡上下文已恢复，请稍候重载当前全景。')}
                />
              ) : (
                <div className="cloud-viewer-placeholder">
                  <strong>{currentPano ? assetStatus(currentPano.status, currentPano.tileState) : '尚未选择场景'}</strong>
                  <span>{currentPano ? '预览生成完成后会在这里显示。' : '上传全景图后开始编辑热点。'}</span>
                </div>
              )}
            </section>

            <aside className="cloud-inspector">
              <section className="cloud-panel">
                <div className="cloud-panel-header compact"><div><h2>当前场景</h2><span>{currentPano ? localizedPanoName(document, currentPano, editingLocale) : '未选择'}</span></div></div>
                <button type="button" className="cloud-secondary block" disabled={!currentPano} onClick={() => entryAudioInputRef.current?.click()}>设置入场语音</button>
                <input ref={entryAudioInputRef} type="file" hidden accept="audio/*" onChange={setEntryAudio} />
                {currentPano?.entryAudioName && <p className="cloud-meta">入场语音：{currentPano.entryAudioName}</p>}
              </section>

              <section className="cloud-panel">
                <div className="cloud-panel-header compact"><div><h2>添加导航点</h2><span>点击全景放置</span></div></div>
                <label className="cloud-field"><span>{editingLocale === 'en' ? '英文名称' : '名称'}</span><input disabled={!currentPano} value={stationName} onChange={(event) => setStationName(event.target.value)} placeholder={editingLocale === 'en' ? 'e.g. Go to Showroom' : '例如：前往展厅'} /></label>
                <label className="cloud-field"><span>目标场景</span><select disabled={!currentPano || otherPanos.length === 0} value={targetPanoId} onChange={(event) => setTargetPanoId(event.target.value)}>{otherPanos.map((pano) => <option key={pano.id} value={pano.id}>{localizedPanoName(document, pano, editingLocale)}</option>)}</select></label>
                <button type="button" className="cloud-secondary block" disabled={!currentPano} onClick={() => stationAudioInputRef.current?.click()}>{pendingAudio ? `语音：${pendingAudio.name}` : '选择站点语音'}</button>
                <input ref={stationAudioInputRef} type="file" hidden accept="audio/*" onChange={setStationAudio} />
                <div className="cloud-control-row"><button type="button" className={`cloud-primary ${placeMode ? 'active' : ''}`} disabled={!currentPano || !targetPanoId} onClick={beginPlacement}>{placeMode ? '等待点击...' : '放置导航点'}</button>{placeMode && <button type="button" className="cloud-secondary" onClick={() => setPlaceMode(false)}>取消</button>}</div>
              </section>

              <section className="cloud-panel cloud-stations-panel">
                <div className="cloud-panel-header compact"><div><h2>导航点</h2><span>{currentStations.length} 个</span></div></div>
                <div className="cloud-station-list">
                  {currentStations.map((station) => {
                    const stationNameValue = localizedStationName(document, currentPano.id, station, editingLocale, false);
                    const stationDisplayName = localizedStationName(document, currentPano.id, station, editingLocale);
                    const target = document.panos.find((pano) => pano.id === station.targetPanoId);
                    return <div className="cloud-station-row" key={station.id}><div><input className="cloud-station-name-input" key={`${editingLocale}-${stationNameValue}`} defaultValue={stationNameValue} placeholder={editingLocale === 'en' ? `英文名称（中文：${station.name}）` : ''} maxLength="120" aria-label={`${editingLocale === 'en' ? '英文' : ''}导航点名称 ${station.name}`} onBlur={(event) => { event.currentTarget.value = renameStation(currentPano.id, station.id, event.currentTarget.value); }} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') { event.currentTarget.value = localizedStationName(documentRef.current, currentPano.id, station, editingLocale, false); event.currentTarget.blur(); } }} /><small>前往 {target ? localizedPanoName(document, target, editingLocale) : station.targetPanoId}</small></div><div><button type="button" className="cloud-secondary compact" onClick={() => selectStation(station.id)}>前往</button><button type="button" className="cloud-icon-button" aria-label={`删除 ${stationDisplayName}`} title="删除导航点" onClick={() => removeStation(station.id)}>×</button></div></div>;
                  })}
                  {currentPano && currentStations.length === 0 && <p className="cloud-empty">暂无导航点。</p>}
                </div>
              </section>
            </aside>
          </div>
        )}
      </section>
      {publishDialogOpen && (
        <div className="cloud-dialog-backdrop" role="presentation" onMouseDown={() => { if (!publishing) setPublishDialogOpen(false); }}>
          <section className="cloud-release-dialog" role="dialog" aria-modal="true" aria-labelledby="publish-dialog-title" onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div><h2 id="publish-dialog-title">发布公开链接</h2><p>本次内容会冻结为独立快照，之后继续编辑不会改变这个链接。</p></div>
              <button type="button" className="cloud-icon-button" onClick={() => setPublishDialogOpen(false)} disabled={publishing} aria-label="关闭发布窗口" title="关闭">×</button>
            </header>
            <label className="cloud-field">
              <span>发布语言</span>
              <div className="cloud-segmented" role="group" aria-label="发布语言">
                <button type="button" className={publishLocale === 'zh-CN' ? 'active' : ''} aria-pressed={publishLocale === 'zh-CN'} onClick={() => setPublishLocale('zh-CN')}>中文</button>
                <button type="button" className={publishLocale === 'en' ? 'active' : ''} aria-pressed={publishLocale === 'en'} onClick={() => setPublishLocale('en')}>English</button>
              </div>
            </label>
            <label className="cloud-field">
              <span>发布备注</span>
              <input autoFocus value={publishNote} onChange={(event) => setPublishNote(event.target.value)} maxLength="160" placeholder={publishLocale === 'en' ? '例如：English website version' : '例如：中文官网正式版 2026-09'} />
            </label>
            <div className="cloud-release-summary">
              <span>{document.panos.length} 个场景</span>
              <span>{document.sceneFolders.length} 个文件夹</span>
              <span>{publishLocale === 'en' ? 'English' : '中文'}</span>
            </div>
            {publishLocale === 'en' && englishIssues.length > 0 && (
              <div className="cloud-translation-warning" role="alert">
                <strong>还有 {englishIssues.length} 项英文未填写</strong>
                <ul>{englishIssues.slice(0, 8).map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}</ul>
                {englishIssues.length > 8 && <small>另有 {englishIssues.length - 8} 项，请切换到 English 后继续补充。</small>}
              </div>
            )}
            <div className="cloud-dialog-actions">
              <button type="button" className="cloud-secondary" onClick={() => setPublishDialogOpen(false)} disabled={publishing}>取消</button>
              <button type="button" className="cloud-primary" onClick={publish} disabled={publishing || !publishNote.trim() || (publishLocale === 'en' && englishIssues.length > 0)}>{publishing ? '发布中...' : `确认发布${publishLocale === 'en' ? '英文' : '中文'}链接`}</button>
            </div>
          </section>
        </div>
      )}
      {releaseHistoryOpen && (
        <div className="cloud-dialog-backdrop" role="presentation" onMouseDown={() => setReleaseHistoryOpen(false)}>
          <section className="cloud-release-history" role="dialog" aria-modal="true" aria-labelledby="release-history-title" onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div><h2 id="release-history-title">已发布链接</h2><p>每次发布都是固定快照。撤销只会关闭选中的公开链接。</p></div>
              <button type="button" className="cloud-icon-button" onClick={() => setReleaseHistoryOpen(false)} aria-label="关闭发布历史" title="关闭">×</button>
            </header>
            <div className="cloud-release-list">
              {displayedReleases.map((release) => {
                const url = releaseUrl(release);
                const active = release.state === 'published';
                return (
                  <article className={`cloud-release-row ${active ? '' : 'is-revoked'}`} key={release.id}>
                    <div className="cloud-release-row-heading">
                      <div><strong>{release.note}</strong><span className={`cloud-release-status ${active ? 'active' : ''}`}>{active ? '公开中' : '已撤销'}</span><span className="cloud-locale-badge">{release.locale === 'en' ? 'English' : '中文'}</span></div>
                      <small>{dateLabel(release.createdAt)}</small>
                    </div>
                    <div className="cloud-release-meta"><span>{release.sceneCount} 个场景</span>{release.createdByName && <span>发布人：{release.createdByName}</span>}</div>
                    {active ? <a className="cloud-release-url" href={url} target="_blank" rel="noreferrer">{url}</a> : <span className="cloud-release-url muted">链接已停止公开访问</span>}
                    {editingReleaseId === release.id ? (
                      <div className="cloud-release-note-editor">
                        <input autoFocus value={releaseNoteDraft} onChange={(event) => setReleaseNoteDraft(event.target.value)} maxLength="160" aria-label="修改发布备注" />
                        <button type="button" className="cloud-secondary compact" onClick={() => saveReleaseNote(release.id)} disabled={!releaseNoteDraft.trim() || releaseActionId === release.id}>保存</button>
                        <button type="button" className="cloud-text-button" onClick={() => { setEditingReleaseId(null); setReleaseNoteDraft(''); }} disabled={releaseActionId === release.id}>取消</button>
                      </div>
                    ) : (
                      <div className="cloud-release-actions">
                        {active && <a className="cloud-secondary compact" href={url} target="_blank" rel="noreferrer">打开</a>}
                        {active && <button type="button" className="cloud-secondary compact" onClick={() => copyReleaseLink(release)}>复制链接</button>}
                        <button type="button" className="cloud-secondary compact" onClick={() => { setEditingReleaseId(release.id); setReleaseNoteDraft(release.note); setConfirmingRevokeId(null); }}>修改备注</button>
                        {active && <button type="button" className="cloud-danger-button compact" onClick={() => { setConfirmingRevokeId(release.id); setEditingReleaseId(null); }}>撤销公开</button>}
                      </div>
                    )}
                    {confirmingRevokeId === release.id && (
                      <div className="cloud-release-revoke-confirm" role="alertdialog" aria-label="确认撤销公开链接">
                        <p>撤销后此链接将立即无法访问，其他发布链接不受影响。</p>
                        <div><button type="button" className="cloud-secondary compact" onClick={() => setConfirmingRevokeId(null)} disabled={releaseActionId === release.id}>取消</button><button type="button" className="cloud-danger-button compact" onClick={() => revokeRelease(release.id)} disabled={releaseActionId === release.id}>{releaseActionId === release.id ? '撤销中...' : '确认撤销'}</button></div>
                      </div>
                    )}
                  </article>
                );
              })}
              {releases.length === 0 && <p className="cloud-empty">这个项目还没有发布过公开链接。</p>}
            </div>
            <footer>
              {releases.length > 5 ? <button type="button" className="cloud-text-button" onClick={() => setShowAllReleases((current) => !current)}>{showAllReleases ? '收起记录' : `查看更多（${releases.length - 5}）`}</button> : <span />}
              <button type="button" className="cloud-primary" onClick={() => setReleaseHistoryOpen(false)}>完成</button>
            </footer>
          </section>
        </div>
      )}
      {confirmingAllPanosDelete && (
        <div className="cloud-dialog-backdrop" role="presentation" onMouseDown={() => { if (!deletingAllPanos) setConfirmingAllPanosDelete(false); }}>
          <section className="cloud-confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-all-panos-title" onMouseDown={(event) => event.stopPropagation()}>
            <h2 id="delete-all-panos-title">删除全部全景图？</h2>
            <p>将从当前草稿移除全部 {document.panos.length} 张全景图和相关导航点。手动版本或公开链接仍引用的素材会保留。</p>
            <div className="cloud-dialog-actions">
              <button type="button" className="cloud-secondary" onClick={() => setConfirmingAllPanosDelete(false)} disabled={deletingAllPanos}>取消</button>
              <button type="button" className="cloud-danger-button" onClick={deleteAllPanos} disabled={deletingAllPanos}>{deletingAllPanos ? '删除中...' : '确认删除全部'}</button>
            </div>
          </section>
        </div>
      )}
      {activeFolderPicker && (
        <div className="cloud-dialog-backdrop" role="presentation" onMouseDown={() => setFolderPickerId(null)}>
          <section className="cloud-folder-picker" role="dialog" aria-modal="true" aria-labelledby="folder-picker-title" onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div><h2 id="folder-picker-title">选择「{localizedFolderName(document, activeFolderPicker, editingLocale)}」中的全景图</h2><p>同一张全景图可以同时加入多个文件夹。</p></div>
              <button type="button" className="cloud-icon-button" onClick={() => setFolderPickerId(null)} aria-label="关闭全景图选择器" title="关闭">×</button>
            </header>
            <label className="cloud-scene-search"><span>查找全景图</span><input type="search" autoFocus value={folderPickerQuery} onChange={(event) => setFolderPickerQuery(event.target.value)} placeholder="输入名称或编号" /></label>
            <div className="cloud-folder-picker-grid">
              {folderPickerPanos.map((pano) => {
                const checked = activeFolderPicker.panoIds.includes(pano.id);
                return <label className={`cloud-folder-picker-item ${checked ? 'is-selected' : ''}`} key={pano.id}>
                  <input type="checkbox" checked={checked} onChange={(event) => setPanoInFolder(activeFolderPicker.id, pano.id, event.target.checked)} />
                  {pano.thumbnailUrl ? <img src={pano.thumbnailUrl} alt="" loading="lazy" /> : <span className="cloud-folder-picker-placeholder" />}
                  <span><strong>{localizedPanoName(document, pano, editingLocale)}</strong><small>{assetStatus(pano.status, pano.tileState)}</small></span>
                </label>;
              })}
              {folderPickerPanos.length === 0 && <p className="cloud-empty">没有符合条件的全景图。</p>}
            </div>
            <footer><span>已选择 {activeFolderPicker.panoIds.length} 张，列表中的第一张是入口图。</span><button type="button" className="cloud-primary" onClick={() => setFolderPickerId(null)}>完成</button></footer>
          </section>
        </div>
      )}
    </Shell>
  );
}

function positiveInteger(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function powerOfTwo(value) {
  return value > 0 && (value & (value - 1)) === 0;
}

function resolvePublicMediaUrl(value) {
  const url = String(value || '').trim();
  if (!url || /^(?:blob|data|javascript):/i.test(url)) return null;
  if (/^(?:https?:)?\/\//i.test(url)) return url;

  const publicApiBase = getCloudConfig().publicApiBase;
  if (
    /^\//.test(url)
    && publicApiBase
    && !/^https?:\/\//i.test(publicApiBase)
    && (url === publicApiBase || url.startsWith(`${publicApiBase}/`))
  ) {
    return url;
  }
  return publicApiPath(url.startsWith('/') ? url : `/${url}`);
}

function normalizePublicTileSource(source) {
  if (!source || typeof source !== 'object' || source.type !== 'equirectangular-tiles') return null;

  const baseUrl = resolvePublicMediaUrl(source.baseUrl);
  const tileUrlTemplate = resolvePublicMediaUrl(source.tileUrlTemplate);
  if (!baseUrl || !tileUrlTemplate) return null;
  if (!['level', 'row', 'col'].every((token) => tileUrlTemplate.includes(`{${token}}`))) return null;

  const levels = Array.isArray(source.levels)
    ? source.levels.map((level) => {
        const width = positiveInteger(level?.width);
        const cols = positiveInteger(level?.cols);
        const rows = positiveInteger(level?.rows);
        return width && cols && rows && powerOfTwo(cols) && powerOfTwo(rows)
          ? { width, cols, rows }
          : null;
      }).filter(Boolean)
    : [];
  if (!levels.length) return null;

  return {
    type: 'equirectangular-tiles',
    baseUrl,
    tileUrlTemplate,
    levels: [...levels].sort((left, right) => left.width - right.width),
  };
}

function legacyPublicPanoramaUrl(pano) {
  const candidates = [pano?.url, pano?.previewUrl, typeof pano?.src === 'string' ? pano.src : null];
  return candidates.map(resolvePublicMediaUrl).find(Boolean) || null;
}

export function publicDocument(payload, publicId) {
  const root = payload?.tour || payload?.release || payload || {};
  const source = root.document || root.manifest?.document || root.manifest || payload?.document || payload?.manifest || root;
  const mediaBase = `/tours/${encodeURIComponent(publicId)}/media`;
  const publicSource = {
    ...source,
    currentId: source.currentId || source.startPanoId,
    panos: (source.panos || []).map((pano) => ({
      ...pano,
      url: normalizePublicTileSource(pano.src)
        || legacyPublicPanoramaUrl(pano)
        || (pano.assetId ? publicApiPath(`${mediaBase}/${encodeURIComponent(pano.assetId)}/preview`) : null),
      previewUrl: pano.assetId
        ? publicApiPath(`${mediaBase}/${encodeURIComponent(pano.assetId)}/preview`)
        : null,
      entryAudioUrl: pano.entryAudioUrl || pano.entryAudioSrc,
    })),
    stationsByPano: Object.fromEntries(Object.entries(source.stationsByPano || {}).map(([panoId, stations]) => [
      panoId,
      (stations || []).map((station) => ({ ...station, audioUrl: station.audioUrl || station.audioSrc })),
    ])),
  };
  const document = normalizeDocument(publicSource);
  return {
    title: root.title || payload?.title || 'VR 全景导览',
    locale: root.locale === 'en' ? 'en' : 'zh-CN',
    document: {
      ...document,
      panos: document.panos.map((pano) => ({
        ...pano,
        url: pano.url || (pano.assetId ? publicApiPath(`${mediaBase}/${encodeURIComponent(pano.assetId)}/preview`) : null),
        entryAudioUrl: pano.entryAudioUrl || (pano.entryAudioAssetId ? publicApiPath(`${mediaBase}/${encodeURIComponent(pano.entryAudioAssetId)}/original`) : null),
      })),
      stationsByPano: Object.fromEntries(Object.entries(document.stationsByPano).map(([panoId, stations]) => [
        panoId,
        stations.map((station) => ({
          ...station,
          audioUrl: station.audioUrl || (station.audioAssetId ? publicApiPath(`${mediaBase}/${encodeURIComponent(station.audioAssetId)}/original`) : null),
        })),
      ])),
    },
  };
}

const PUBLIC_COPY = {
  'zh-CN': {
    sceneCount: (count) => `${count} 个场景`,
    allScenes: '总场景',
    folderNavigation: '公司楼层导航',
  },
  en: {
    sceneCount: (count) => `${count} ${count === 1 ? 'scene' : 'scenes'}`,
    allScenes: 'All Scenes',
    folderNavigation: 'Tour sections',
  },
};

function PublicTour({ publicId }) {
  const audioPlayerRef = React.useRef(null);
  const [tour, setTour] = React.useState(null);
  const [activeSceneId, setActiveSceneId] = React.useState(null);
  const [activeFolderId, setActiveFolderId] = React.useState(null);
  const [error, setError] = React.useState('');

  const stopAudio = React.useCallback(() => {
    const player = audioPlayerRef.current;
    if (player) {
      player.pause();
      player.currentTime = 0;
      audioPlayerRef.current = null;
    }
  }, []);

  React.useEffect(() => () => stopAudio(), [stopAudio]);

  React.useEffect(() => {
    if (!tour?.locale) return undefined;
    const previous = document.documentElement.lang;
    document.documentElement.lang = tour.locale;
    return () => { document.documentElement.lang = previous; };
  }, [tour?.locale]);

  React.useEffect(() => {
    let active = true;
    cloudApi.publicManifest(publicId)
      .then((payload) => {
        if (!active) return;
        const next = publicDocument(payload, publicId);
        const initialSceneId = next.document.currentId || next.document.panos[0]?.id || null;
        setTour(next);
        setActiveSceneId(initialSceneId);
        setActiveFolderId(null);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || '无法加载公开导览');
      });
    return () => { active = false; };
  }, [publicId]);

  const currentPano = tour?.document.panos.find((pano) => pano.id === activeSceneId) || tour?.document.panos[0] || null;
  const publicFolders = React.useMemo(() => {
    if (!tour) return [];
    const panoIds = new Set(tour.document.panos.map((pano) => pano.id));
    return tour.document.sceneFolders
      .map((folder) => ({ ...folder, panoIds: folder.panoIds.filter((panoId) => panoIds.has(panoId)) }))
      .filter((folder) => folder.panoIds.length > 0);
  }, [tour]);
  const stations = React.useMemo(() => {
    if (!currentPano || !tour) return [];
    return tour.document.stationsByPano[currentPano.id] || [];
  }, [currentPano, tour]);
  const copy = PUBLIC_COPY[tour?.locale] || PUBLIC_COPY['zh-CN'];

  const selectStation = React.useCallback((stationId) => {
    const station = stations.find((item) => item.id === stationId);
    if (!station) return;
    if (station.audioUrl) {
      stopAudio();
      const player = new Audio(station.audioUrl);
      audioPlayerRef.current = player;
      player.play().catch(() => undefined);
    }
    const target = tour?.document.panos.find((pano) => pano.id === station.targetPanoId);
    if (target?.url) {
      setActiveSceneId(target.id);
      setActiveFolderId((currentFolderId) => {
        if (!currentFolderId) return null;
        const currentFolder = publicFolders.find((folder) => folder.id === currentFolderId);
        if (currentFolder?.panoIds.includes(target.id)) return currentFolderId;
        return null;
      });
    }
  }, [publicFolders, stations, stopAudio, tour]);

  const enterAllScenes = React.useCallback(() => {
    const entryPano = tour?.document.panos.find((pano) => pano.id === tour.document.currentId)
      || tour?.document.panos[0];
    if (!entryPano?.url) return;
    setActiveFolderId(null);
    setActiveSceneId(entryPano.id);
  }, [tour]);

  const enterFolder = React.useCallback((folder) => {
    const entryPano = tour?.document.panos.find((pano) => pano.id === folder.panoIds[0]);
    if (!entryPano?.url) return;
    setActiveFolderId(folder.id);
    setActiveSceneId(entryPano.id);
  }, [tour]);

  if (error) return <Shell className="cloud-centered"><Message kind="error">{error}</Message></Shell>;
  if (!tour || !currentPano) return <Shell className="cloud-centered"><div className="cloud-loading">VR TOUR</div></Shell>;

  return (
    <main className="cloud-public-tour">
      <header className="cloud-public-header"><div><div className="cloud-eyebrow">VR TOUR</div><h1>{tour.title}</h1></div><div className="cloud-public-count">{copy.sceneCount(tour.document.panos.length)}</div></header>
      <PanoramaViewer src={currentPano.url} stations={stations} onSelectStation={selectStation} onError={() => setError(tour.locale === 'en' ? 'Failed to load panorama' : '全景加载失败')} />
      {publicFolders.length > 0 && (
        <nav className="cloud-public-folders" aria-label={copy.folderNavigation}>
          <button type="button" className={activeFolderId ? '' : 'active'} aria-current={activeFolderId ? undefined : 'page'} onClick={enterAllScenes}>
            <span className="cloud-public-all-shape" aria-hidden="true" />
            <span><strong>{copy.allScenes}</strong><small>{copy.sceneCount(tour.document.panos.length)}</small></span>
          </button>
          {publicFolders.map((folder) => (
            <button type="button" key={folder.id} className={folder.id === activeFolderId ? 'active' : ''} aria-current={folder.id === activeFolderId ? 'page' : undefined} onClick={() => enterFolder(folder)}>
              <span className="cloud-public-folder-shape" aria-hidden="true" />
              <span><strong>{folder.name}</strong><small>{copy.sceneCount(folder.panoIds.length)}</small></span>
            </button>
          ))}
        </nav>
      )}
    </main>
  );
}

export default function CloudApp() {
  const publicId = publicTourIdFromPath(window.location.pathname);
  return publicId ? <PublicTour publicId={publicId} /> : <CloudEditor />;
}
